import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  getCountFromServer,
  getAggregateFromServer,
  count,
  sum,
  limit,
  startAfter,
  documentId
} from 'firebase/firestore';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db, functions, auth } from '../firebase';
import { httpsCallable, type HttpsCallableOptions } from 'firebase/functions';
import { isAdminEmail } from '../config/adminConfig';
import { DeviceInfo, DeviceInfoService } from './deviceInfoService';
import { auditLogService } from './auditLogService';
import { getProMonthlyPrice } from '../config/proPlanPricing';
import { BUSINESS_MONTHLY_PRICE } from '../config/proPlanPricing';

/** Match Cloud Function max timeout (9 min) — default client timeout is ~70s and causes bulk delete to fail. */
const ADMIN_DELETE_CALLABLE_OPTIONS: HttpsCallableOptions = { timeout: 540000 };

export type DeleteUserAccountResult = {
  success: boolean;
  userId: string;
  email?: string;
  authDeleted: boolean;
  ownedWorkspacesDeleted: number;
  deletedCounts: Record<string, number>;
  totalDocumentOperations: number;
};

export type BulkDeleteUserResultItem = {
  userId: string;
  success: boolean;
  email?: string;
  error?: string;
  authDeleted?: boolean;
  ownedWorkspacesDeleted?: number;
  totalDocumentOperations?: number;
};

export type BulkDeleteUsersResult = {
  success: boolean;
  successCount: number;
  failedCount: number;
  results: BulkDeleteUserResultItem[];
};

export interface AdminUser {
  id: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  status: 'active' | 'pending' | 'suspended' | 'blocked';
  invitedBy?: string;
  invitedAt?: Timestamp;
  lastActive?: Timestamp;
  createdAt: Timestamp;
  lastLoginAt?: Timestamp;
  twoFactorEnabled: boolean;
  emailVerified?: boolean;
  // Additional profile information
  displayName?: string;
  firstName?: string;
  lastName?: string;
  onboardingCompleted?: boolean;
  companyName?: string;
  industry?: string;
  // Device information
  deviceInfo?: DeviceInfo;
  // Blocking information
  blockedAt?: Timestamp;
  blockedBy?: string;
  blockReason?: string;
  // Subscription information
  subscription?: {
    plan: 'starter' | 'pro' | 'business' | 'enterprise' | 'free';
    status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid';
    subscribers?: number;
    emails?: number;
    currency?: string;
    startDate?: Timestamp;
    currentPeriodStart?: Timestamp;
    currentPeriodEnd?: Timestamp;
    cancelAtPeriodEnd?: boolean;
    updatedAt?: Timestamp;
  };
  // Marketing source (from shareable signup links)
  signupSource?: string;
  signupSourceLabel?: string;
  signupSourceBadge?: string;
  signupSourceBadgeColor?: string;
  signupSourceCampaign?: string;
}

export interface AdminCampaign {
  id: string;
  name: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused';
  createdAt: Timestamp;
  scheduledAt?: Timestamp;
  sentAt?: Timestamp;
  recipientCount: number;
  openRate?: number;
  clickRate?: number;
  userId: string;
  userEmail: string;
  workspaceId?: string;
}

export interface AdminContact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status: 'active' | 'unsubscribed' | 'bounced';
  createdAt: Timestamp;
  lastActivityAt?: Timestamp;
  userId: string;
  userEmail: string;
  source?: 'import' | 'manual' | 'api';
  exported?: boolean;
}

export interface TransactionRecord {
  id: string;
  customerName: string;
  amount: number;
  currency: string;
  tax: number;
  status: 'paid' | 'pending' | 'failed' | 'refunded';
  date: Date;
  invoiceId: string;
}

export interface AdminPayment {
  id: string;
  paymentId: string;
  orderId?: string;
  amount: number;
  currency: string;
  status: 'authorized' | 'captured' | 'failed';
  method?: string;
  email?: string;
  contact?: string;
  createdAt: Timestamp;
  capturedAt?: Timestamp;
  errorCode?: string;
  errorDescription?: string;
}

export interface AdminSubscription {
  id: string;
  userId: string;
  planId: string;
  status: 'active' | 'cancelled' | 'past_due' | 'unpaid' | 'expired';
  currentStart: Timestamp;
  currentEnd: Timestamp;
  createdAt: Timestamp;
  cancelledAt?: Timestamp;
  amount: number;
  currency: string;
  interval: 'monthly' | 'yearly';
  paymentMethod?: string;
  userEmail?: string; // Populated from user join
  paymentProvider?: string;
  /** Razorpay `pay_…` / Stripe `pi_…` from checkout (stored on `users.subscription`). */
  lastPaymentReference?: string;
  dodoSubscriptionId?: string;
  dodoPaymentId?: string;
  stripeSubscriptionId?: string;
  cancelAtPeriodEnd?: boolean;
  subscribersCap?: number;
  emailsPerMonth?: number;
  displayName?: string;
  companyName?: string;
  latestInvoiceId?: string;
  latestInvoiceViewUrl?: string;
  latestInvoiceDownloadUrl?: string;
  /** From latest paid `invoices` row (minor units). Prefer for revenue display + invoice generation. */
  latestInvoiceAmountPaidMinor?: number;
  latestInvoiceCurrency?: string;
  /** Invoice `created` (seconds since epoch) when derived from Firestore invoices. */
  latestInvoiceCreated?: number;
}

/** One page of `users` with active subscription (client-filtered to paid plans in mapper). */
export interface AdminSubscriptionsPageResult {
  subscriptions: AdminSubscription[];
  /** Last doc from this Firestore page — pass as `startAfterDoc` for the next request. */
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}


export interface SystemMetrics {
  totalUsers: number;
  activeUsers: number;
  totalCampaigns: number;
  totalContacts: number;
  emailsSentToday: number;
  emailsSentThisMonth: number;
  systemUptime: number;
  averageResponseTime: number;

  // User subscription metrics
  paidUsers: number;
  unpaidUsers: number;
  freeUsers: number;
  premiumUsers: number;

  // Revenue metrics
  revenueThisMonth: number;
  revenueLastMonth: number;
  revenueLast3Months: number;
  revenueLast6Months: number;
  revenueThisYear: number;
  monthlyRecurringRevenue: number;
  averageRevenuePerUser: number;

  // Growth metrics
  newUsersThisMonth: number;
  newUsersLastMonth: number;
  userGrowthRate: number;
  churnRate: number;
  retentionRate: number;

  // Engagement metrics
  averageContactsPerUser: number;
  averageCampaignsPerUser: number;
  emailOpenRate: number;
  emailClickRate: number;
  unsubscribeRate: number;

  // System performance
  totalEmailsSent: number;
  emailsSentLastMonth: number;
  averageEmailDeliveryTime: number;
  bounceRate: number;

  // Business metrics
  conversionRate: number;
  customerLifetimeValue: number;
  totalRevenue: number;
  monthlyGrowthRate: number;

  // New Comprehensive Metrics
  annualRecurringRevenue: number;
  netRevenue: number;
  revenuePerPlan: {
    starter: number;
    pro: number;
    business: number;
    enterprise: number;
  };
  revenueByBillingCycle: {
    monthly: number;
    yearly: number;
  };
  revenueByPaymentMethod: {
    card: number;
    paypal: number;
    stripe: number;
  };
  revenueByGeography: Record<string, number>;

  // Subscription Health
  newSubscriptionsThisMonth: number;
  cancelledSubscriptionsThisMonth: number;
  trialToPaidConversionRate: number;
  renewalRate: number;
  expansionRevenue: number;
  contractionRevenue: number;

  // Forecast
  revenueForecastNext30Days: number;

  // Invoices
  recentTransactions: TransactionRecord[];
}

export interface EmailServiceStatusResponse {
  ses: {
    status: 'working' | 'error' | 'not_configured' | 'unknown';
    configured: boolean;
    details?: {
      sentLast24Hours: number;
      max24HourSend: number;
      maxSendRate: number;
    };
    stats?: {
      totalSent: number;
      sentToday: number;
      lastSentAt: Timestamp | null;
    };
    error?: string;
  };
  zeptomail: {
    status: 'working' | 'error' | 'not_configured' | 'unknown';
    configured: boolean;
    stats?: {
      totalSent: number;
      sentToday: number;
      lastSentAt: Timestamp | null;
    };
    error?: string;
    code?: number;
  };
}



export class AdminService {
  private static subscriptionPriority(plan: unknown): number {
    const p = String(plan || '').toLowerCase();
    if (p === 'enterprise') return 4;
    if (p === 'business') return 3;
    if (p === 'pro') return 2;
    if (p === 'starter' || p === 'free') return 1;
    return 0;
  }

  private static pickMostAccurateSubscription(
    primary: Record<string, unknown> | null | undefined,
    fallback: Record<string, unknown> | null | undefined
  ): Record<string, unknown> | null {
    const p = primary && typeof primary === 'object' ? primary : null;
    const f = fallback && typeof fallback === 'object' ? fallback : null;
    if (!p && !f) return null;
    if (!p) return f;
    if (!f) return p;

    const pScore = this.subscriptionPriority((p as { plan?: unknown }).plan);
    const fScore = this.subscriptionPriority((f as { plan?: unknown }).plan);
    if (fScore > pScore) return f;
    if (pScore > fScore) return p;

    const pUpdated =
      (p as { updatedAt?: { toDate?: () => Date } }).updatedAt?.toDate?.().getTime?.() || 0;
    const fUpdated =
      (f as { updatedAt?: { toDate?: () => Date } }).updatedAt?.toDate?.().getTime?.() || 0;
    return fUpdated > pUpdated ? f : p;
  }

  // Helper function to check if a user is admin based on email
  static isUserAdmin(email: string): boolean {
    return isAdminEmail(email);
  }

  // Send a manual test email using a specific provider
  static async sendTestEmail(provider: 'ses' | 'zeptomail'): Promise<any> {
    try {
      const sendTestEmailFn = httpsCallable(functions, 'adminSendTestEmail');
      const result = await sendTestEmailFn({ provider });
      return result.data;
    } catch (error) {
      console.error(`Error sending test email via ${provider}:`, error);
      throw error;
    }
  }

  // Update user details in the database
  static async updateUser(userId: string, updates: Partial<AdminUser>): Promise<void> {
    try {
      console.log('Updating user:', userId, 'with updates:', updates);

      // Validate inputs
      if (!userId || userId.trim() === '') {
        throw new Error('User ID is required');
      }

      if (!updates || Object.keys(updates).length === 0) {
        console.log('No updates provided, skipping update');
        return;
      }

      // Prepare user profile updates - only include defined fields
      const userProfileUpdates: any = {
        lastActive: Timestamp.now()
      };

      if (updates.displayName !== undefined) userProfileUpdates.displayName = updates.displayName;
      if (updates.firstName !== undefined) userProfileUpdates.firstName = updates.firstName;
      if (updates.lastName !== undefined) userProfileUpdates.lastName = updates.lastName;
      if (updates.companyName !== undefined) userProfileUpdates.companyName = updates.companyName;
      if (updates.onboardingCompleted !== undefined) userProfileUpdates.onboardingCompleted = updates.onboardingCompleted;
      if (updates.industry !== undefined) userProfileUpdates.industry = updates.industry;

      // Update user profile in 'users' collection
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, userProfileUpdates);
      console.log('User profile updated successfully');

      // Audit log
      const actor = auth.currentUser;
      if (actor) {
        await auditLogService.logEvent(
          'USER_ROLE_UPDATED',
          actor.uid,
          actor.email || 'unknown',
          `Admin updated user profile and status for ${updates.email || userId}`,
          userId,
          { updates }
        );
      }

      // Update user role/status in 'users_invited_by_users' collection
      const accountUserRef = doc(db, 'users_invited_by_users', userId);
      const accountUserDoc = await getDoc(accountUserRef);

      if (accountUserDoc.exists()) {
        // Update existing account user record - only include defined fields
        const accountUserUpdates: any = {
          lastActive: Timestamp.now()
        };

        if (updates.role !== undefined) accountUserUpdates.role = updates.role;
        if (updates.status !== undefined) accountUserUpdates.status = updates.status;
        if (updates.twoFactorEnabled !== undefined) accountUserUpdates.twoFactorEnabled = updates.twoFactorEnabled;

        await updateDoc(accountUserRef, accountUserUpdates);
        console.log('Account user record updated successfully');
      } else {
        // Create new account user record if it doesn't exist
        const newAccountUserData: any = {
          role: updates.role || 'viewer',
          status: updates.status || 'active',
          createdAt: Timestamp.now(),
          lastActive: Timestamp.now(),
          twoFactorEnabled: updates.twoFactorEnabled || false
        };

        await setDoc(accountUserRef, newAccountUserData);
        console.log('New account user record created successfully');
      }

      console.log('User updated successfully:', userId);
    } catch (error) {
      console.error('Error updating user:', error);
      console.error('Error details:', {
        userId,
        updates,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      throw new Error(`Failed to update user details: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // User Management - Clean up duplicate user records
  static async cleanupDuplicateUsers(): Promise<{ cleaned: number; duplicates: string[] }> {
    try {
      const userProfilesSnapshot = await getDocs(collection(db, 'users'));
      const emailMap = new Map<string, { docId: string; lastLogin: Date | null; createdAt: Date }>();
      const duplicates: string[] = [];

      // Find duplicates by email
      userProfilesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const email = data.email?.toLowerCase();
        if (!email) return;

        const lastLogin = data.lastLoginAt?.toDate() || null;
        const createdAt = data.createdAt?.toDate() || new Date();

        if (emailMap.has(email)) {
          const existing = emailMap.get(email)!;
          // Keep the one with the most recent login, or if no login, the most recent creation
          const shouldReplace = lastLogin && existing.lastLogin
            ? lastLogin > existing.lastLogin
            : !existing.lastLogin && createdAt > existing.createdAt;

          if (shouldReplace) {
            duplicates.push(existing.docId);
            emailMap.set(email, { docId: doc.id, lastLogin, createdAt });
          } else {
            duplicates.push(doc.id);
          }
        } else {
          emailMap.set(email, { docId: doc.id, lastLogin, createdAt });
        }
      });

      // Delete duplicate records
      const deletePromises = duplicates.map(docId => deleteDoc(doc(db, 'users', docId)));
      await Promise.all(deletePromises);

      return { cleaned: duplicates.length, duplicates };
    } catch (error) {
      console.error('Error cleaning up duplicate users:', error);
      throw error;
    }
  }

  /** Keep one profile per email (most recent login, else newest created). */
  static dedupeUsersByEmail(users: AdminUser[]): AdminUser[] {
    const emailMap = new Map<string, AdminUser>();
    users.forEach((user) => {
      const email = (user.email || '').trim().toLowerCase();
      if (!email || email === 'no email') {
        emailMap.set(`__no_email__${user.id}`, user);
        return;
      }
      const existing = emailMap.get(email);
      if (!existing) {
        emailMap.set(email, user);
        return;
      }
      const userLogin = user.lastLoginAt?.toDate?.()?.getTime() || 0;
      const existingLogin = existing.lastLoginAt?.toDate?.()?.getTime() || 0;
      if (userLogin && existingLogin && userLogin > existingLogin) {
        emailMap.set(email, user);
        return;
      }
      if (!userLogin && !existingLogin) {
        const userCreated = user.createdAt?.toDate?.()?.getTime() || 0;
        const existingCreated = existing.createdAt?.toDate?.()?.getTime() || 0;
        if (userCreated > existingCreated) emailMap.set(email, user);
      } else if (userLogin && !existingLogin) {
        emailMap.set(email, user);
      }
    });
    return Array.from(emailMap.values());
  }

  // User Management - Get all authenticated users
  // ── Module-level TTL cache — shared across all admin page visits ──────────
  // getAllUsers reads 3 full collections (users, users_invited_by_users,
  // user_settings). Without a cache it re-runs on every admin tab switch.
  // 2-minute TTL is short enough that new signups appear quickly.
  private static _usersCache: AdminUser[] | null = null;
  private static _usersCacheTime = 0;
  private static readonly _USERS_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

  /** Manually bust the cache (call after bulk deletes, status changes, etc.). */
  static invalidateUsersCache(): void {
    AdminService._usersCache = null;
    AdminService._usersCacheTime = 0;
  }

  static async getAllUsers(options?: { dedupeByEmail?: boolean }): Promise<AdminUser[]> {
    const dedupeByEmail = options?.dedupeByEmail !== false;

    // Return cached result if still fresh
    if (AdminService._usersCache && Date.now() - AdminService._usersCacheTime < AdminService._USERS_CACHE_TTL_MS) {
      const cached = dedupeByEmail
        ? AdminService.dedupeUsersByEmail(AdminService._usersCache)
        : AdminService._usersCache;
      return cached;
    }

    try {
      console.log('Fetching all users...');
      // Get all authenticated users from the 'users' collection
      // This collection contains user profiles for all authenticated users
      const userProfilesSnapshot = await getDocs(collection(db, 'users'));
      console.log(`Fetched ${userProfilesSnapshot.size} user profiles`);

      if (userProfilesSnapshot.empty) {
        throw new Error(`CRITICAL: 'users' collection returned 0 documents. Project: ${db.app.options.projectId}`);
      }

      // Get account users for role information (optional role data)
      let accountUsersMap = new Map();
      try {
        const accountUsersSnapshot = await getDocs(collection(db, 'users_invited_by_users'));
        console.log(`Fetched ${accountUsersSnapshot.size} invited user records`);
        accountUsersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          accountUsersMap.set(doc.id, data);
        });
      } catch (err) {
        console.warn('Failed to fetch invited users for enrichment:', err);
      }

      // Subscription fallback source. Some billing flows update user_settings first.
      const userSettingsMap = new Map<string, Record<string, unknown>>();
      try {
        const settingsSnapshot = await getDocs(collection(db, 'user_settings'));
        settingsSnapshot.docs.forEach(doc => {
          userSettingsMap.set(doc.id, doc.data() as Record<string, unknown>);
        });
      } catch (err) {
        console.warn('Failed to fetch user_settings for subscription fallback:', err);
      }

      // Process only authenticated users from 'users' collection
      const authenticatedUsers: AdminUser[] = userProfilesSnapshot.docs.map(doc => {
        try {
          const data = doc.data();
          const accountUser = accountUsersMap.get(doc.id);
          const settingsData = userSettingsMap.get(doc.id);
          const mergedSubscription = this.pickMostAccurateSubscription(
            data.subscription as Record<string, unknown> | undefined,
            (settingsData?.subscription as Record<string, unknown> | undefined) || undefined
          );

          return {
            id: doc.id,
            email: data.email || 'No email',
            role: accountUser?.role || 'viewer',
            status:
              (data.status as AdminUser['status']) ||
              accountUser?.status ||
              'active',
            invitedBy: accountUser?.invitedBy || 'self-registered',
            invitedAt: accountUser?.invitedAt || data.createdAt,
            lastActive: accountUser?.lastActive || data.lastLoginAt,
            twoFactorEnabled: accountUser?.twoFactorEnabled || false,
            createdAt: data.createdAt || Timestamp.now(),
            lastLoginAt: data.lastLoginAt || null,
            // Additional profile information
            displayName: data.displayName,
            firstName: data.firstName,
            lastName: data.lastName,
            onboardingCompleted: data.onboardingCompleted || false,
            companyName: data.companyName,
            industry: data.industry,
            // Device information
            deviceInfo: data.deviceInfo || null,
            // Subscription information
            subscription: mergedSubscription || null,
            // Marketing source
            signupSource: data.signupSource || undefined,
            signupSourceLabel: data.signupSourceLabel || undefined,
            signupSourceBadge: data.signupSourceBadge || undefined,
            signupSourceBadgeColor: data.signupSourceBadgeColor || undefined,
            signupSourceCampaign: data.signupSourceCampaign || undefined,
          } as AdminUser;
        } catch (mapErr) {
          console.error('Error mapping user doc:', doc.id, mapErr);
          return null;
        }
      }).filter(u => u !== null) as AdminUser[];

      const listedUsers = dedupeByEmail
        ? AdminService.dedupeUsersByEmail(authenticatedUsers)
        : authenticatedUsers;

      // Sort by last login date (most recent first), then by creation date
      listedUsers.sort((a, b) => {
        try {
          const aLastLogin = a.lastLoginAt?.toDate?.()?.getTime() || 0;
          const bLastLogin = b.lastLoginAt?.toDate?.()?.getTime() || 0;

          if (aLastLogin !== bLastLogin) {
            return bLastLogin - aLastLogin; // Most recent login first
          }

          // If no login data, sort by creation date
          const aCreated = a.createdAt?.toDate?.()?.getTime() || 0;
          const bCreated = b.createdAt?.toDate?.()?.getTime() || 0;
          return bCreated - aCreated;
        } catch (sortErr) {
          return 0; // Safe fallback
        }
      });

      console.log(
        dedupeByEmail
          ? `Returning ${listedUsers.length} unique users (${authenticatedUsers.length} profiles in Firestore)`
          : `Returning ${listedUsers.length} user profiles (all documents in users collection)`
      );

      // Store the raw (non-deduped) list in cache; deduplication is cheap and
      // applied on the fly so both callers share the same underlying cache entry.
      AdminService._usersCache = authenticatedUsers;
      AdminService._usersCacheTime = Date.now();

      return listedUsers;
    } catch (error) {
      console.error('Error fetching authenticated users:', error);
      // Return empty array instead of throwing to prevent dashboard crash, 
      // but log error so we know.
      // Or throw if we want the error UI. 
      // Given the issue, let's return [] to avoid full crash, but maybe the user WANTS the crash to see the error?
      // "No user found" implies empty array returned.
      throw error;
    }
  }

  static async getUserById(userId: string): Promise<AdminUser | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const settingsRef = doc(db, 'user_settings', userId);
      const [userDoc, settingsDoc] = await Promise.all([getDoc(userRef), getDoc(settingsRef)]);
      if (userDoc.exists()) {
        const userData = userDoc.data() as Record<string, unknown>;
        const settingsData = settingsDoc.exists() ? (settingsDoc.data() as Record<string, unknown>) : null;
        const mergedSubscription = this.pickMostAccurateSubscription(
          userData.subscription as Record<string, unknown> | undefined,
          (settingsData?.subscription as Record<string, unknown> | undefined) || undefined
        );
        return {
          id: userDoc.id,
          ...userData,
          subscription: mergedSubscription || userData.subscription || null,
        } as AdminUser;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  static async getUserIdByEmail(email: string): Promise<string | null> {
    try {
      const q = query(collection(db, 'users'), where('email', '==', email));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return snapshot.docs[0].id;
      }

      // Fallback to invited users
      const q2 = query(collection(db, 'users_invited_by_users'), where('email', '==', email));
      const snapshot2 = await getDocs(q2);

      if (!snapshot2.empty) {
        return snapshot2.docs[0].id;
      }

      return null;
    } catch (error) {
      console.error('Error fetching user by email:', error);
      return null;
    }
  }

  static async updateUserRole(userId: string, role: 'admin' | 'editor' | 'viewer'): Promise<void> {
    try {
      const userRef = doc(db, 'users_invited_by_users', userId);
      await updateDoc(userRef, { role });
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  static async updateUserStatus(userId: string, status: 'active' | 'pending' | 'suspended'): Promise<void> {
    try {
      const userRef = doc(db, 'users_invited_by_users', userId);
      await updateDoc(userRef, { status });
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  /** Permanently delete a user account and all associated data (server-side). */
  static async deleteUser(userId: string): Promise<DeleteUserAccountResult> {
    try {
      console.log('AdminService.deleteUser: Full account deletion for:', userId);

      const deleteFn = httpsCallable<{ userId: string }, DeleteUserAccountResult>(
        functions,
        'adminDeleteUserAccount',
        ADMIN_DELETE_CALLABLE_OPTIONS
      );
      const result = await deleteFn({ userId });
      const data = result.data;

      if (!data?.success) {
        throw new Error('User deletion did not complete successfully');
      }

      const actor = auth.currentUser;
      if (actor) {
        await auditLogService.logEvent(
          'USER_DELETED',
          actor.uid,
          actor.email || 'unknown',
          `Admin permanently deleted user account: ${data.email || userId} (${data.totalDocumentOperations} doc operations)`,
          userId
        );
      }

      return data;
    } catch (error) {
      console.error('Error deleting user:', error);
      const err = error as { code?: string; message?: string };
      if (err?.code === 'functions/not-found') {
        throw new Error(
          'Delete function is not deployed. Run: firebase deploy --only functions:adminDeleteUserAccount'
        );
      }
      if (err?.code === 'functions/permission-denied') {
        throw new Error(err.message || 'Only administrators can delete user accounts.');
      }
      if (err?.code === 'functions/failed-precondition') {
        throw new Error(err.message || 'This account cannot be deleted.');
      }
      throw new Error(err?.message || (error instanceof Error ? error.message : 'Failed to delete user'));
    }
  }

  /** Permanently delete multiple user accounts (one full cascade per user, sequential). */
  static async deleteUsers(
    userIds: string[],
    onProgress?: (completed: number, total: number, userId: string) => void
  ): Promise<BulkDeleteUsersResult> {
    const uniqueIds = Array.from(new Set(userIds.map((id) => id.trim()).filter(Boolean)));
    if (uniqueIds.length === 0) {
      throw new Error('Select at least one user to delete');
    }

    const deleteFn = httpsCallable<{ userId: string }, DeleteUserAccountResult>(
      functions,
      'adminDeleteUserAccount',
      ADMIN_DELETE_CALLABLE_OPTIONS
    );

    const results: BulkDeleteUserResultItem[] = [];

    for (let i = 0; i < uniqueIds.length; i++) {
      const userId = uniqueIds[i];
      onProgress?.(i, uniqueIds.length, userId);

      try {
        const result = await deleteFn({ userId });
        const data = result.data;

        if (!data?.success) {
          results.push({
            userId,
            success: false,
            error: 'Deletion did not complete successfully',
          });
          continue;
        }

        results.push({
          userId,
          success: true,
          email: data.email || undefined,
          authDeleted: data.authDeleted,
          ownedWorkspacesDeleted: data.ownedWorkspacesDeleted,
          totalDocumentOperations: data.totalDocumentOperations,
        });
      } catch (error) {
        console.error(`Error deleting user ${userId}:`, error);
        const err = error as { code?: string; message?: string };
        let message = err?.message || (error instanceof Error ? error.message : 'Failed to delete user');
        if (err?.code === 'functions/deadline-exceeded') {
          message = 'Timed out — this account may have a large amount of data. Try deleting it alone from User Details.';
        }
        if (err?.code === 'functions/not-found') {
          message = 'Delete function is not deployed. Run: firebase deploy --only functions:adminDeleteUserAccount';
        }
        results.push({ userId, success: false, error: message });
      }
    }

    onProgress?.(uniqueIds.length, uniqueIds.length, '');

    const successCount = results.filter((r) => r.success).length;
    const failedCount = results.length - successCount;

    const actor = auth.currentUser;
    if (actor && successCount > 0) {
      await auditLogService.logEvent(
        'USER_DELETED',
        actor.uid,
        actor.email || 'unknown',
        `Admin bulk deleted ${successCount} user account(s) (${failedCount} failed)`,
        uniqueIds.join(',')
      );
    }

    return {
      success: failedCount === 0,
      successCount,
      failedCount,
      results,
    };
  }

  // Campaign Management
  static async getAllCampaigns(): Promise<AdminCampaign[]> {
    try {
      console.log('AdminService.getAllCampaigns: Starting to fetch campaigns...');

      // Query only sent campaigns - filter by status 'sent'
      const campaignsQuery = query(
        collection(db, 'campaigns'),
        where('status', '==', 'sent')
      );
      const campaignsSnapshot = await getDocs(campaignsQuery);

      console.log(`AdminService.getAllCampaigns: Found ${campaignsSnapshot.docs.length} sent campaigns`);

      const campaigns = [];
      for (const campaignDoc of campaignsSnapshot.docs) {
        try {
          const campaignData = campaignDoc.data();
          console.log(`AdminService.getAllCampaigns: Processing campaign ${campaignDoc.id}`, {
            name: campaignData.name,
            userId: campaignData.userId,
            workspaceId: campaignData.workspaceId,
            status: campaignData.status
          });

          let userEmail = 'Unknown';

          // Try to get user email from 'users' collection first
          if (campaignData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', campaignData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                userEmail = userData.email || 'No email';
                console.log(`AdminService.getAllCampaigns: Found user email from 'users' collection: ${userEmail}`);
              } else {
                // Fallback to users_invited_by_users
                const invitedUserDoc = await getDoc(doc(db, 'users_invited_by_users', campaignData.userId));
                if (invitedUserDoc.exists()) {
                  const invitedUserData = invitedUserDoc.data();
                  userEmail = invitedUserData.email || 'No email';
                  console.log(`AdminService.getAllCampaigns: Found user email from 'users_invited_by_users' collection: ${userEmail}`);
                }
              }
            } catch (userError) {
              console.error(`AdminService.getAllCampaigns: Error fetching user email for ${campaignData.userId}:`, userError);
            }
          }

          campaigns.push({
            id: campaignDoc.id,
            name: campaignData.name || 'Untitled Campaign',
            status: campaignData.status || 'sent',
            createdAt: campaignData.createdAt,
            scheduledAt: campaignData.scheduledAt,
            sentAt: campaignData.sentAt,
            recipientCount: campaignData.statistics?.totalRecipients || campaignData.recipientCount || 0,
            openRate: campaignData.statistics?.openRate || campaignData.openRate,
            clickRate: campaignData.statistics?.clickRate || campaignData.clickRate,
            userId: campaignData.userId,
            workspaceId: campaignData.workspaceId,
            userEmail
          } as AdminCampaign);
        } catch (campaignError) {
          console.error(`AdminService.getAllCampaigns: Error processing campaign ${campaignDoc.id}:`, campaignError);
          // Continue with other campaigns even if one fails
        }
      }

      // Sort by createdAt manually (newest first)
      campaigns.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;

        const aTime = a.createdAt.toDate ? a.createdAt.toDate().getTime() : (a.createdAt.seconds * 1000);
        const bTime = b.createdAt.toDate ? b.createdAt.toDate().getTime() : (b.createdAt.seconds * 1000);
        return bTime - aTime;
      });

      console.log(`AdminService.getAllCampaigns: Successfully processed ${campaigns.length} sent campaigns`);
      return campaigns;
    } catch (error) {
      console.error('AdminService.getAllCampaigns: Error fetching campaigns:', error);
      throw error;
    }
  }

  static async getCampaignById(campaignId: string): Promise<AdminCampaign | null> {
    try {
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
      if (campaignDoc.exists()) {
        const campaignData = campaignDoc.data();

        // Get user email for the campaign
        const userDoc = await getDoc(doc(db, 'users_invited_by_users', campaignData.userId));
        const userEmail = userDoc.exists() ? userDoc.data().email : 'Unknown';

        return {
          id: campaignDoc.id,
          ...campaignData,
          userEmail
        } as AdminCampaign;
      }
      return null;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      throw error;
    }
  }

  // Contact Management
  static async getAllContacts(): Promise<AdminContact[]> {
    try {
      const contactsSnapshot = await getDocs(
        query(collection(db, 'contacts'), orderBy('createdAt', 'desc'))
      );

      const contacts = [];
      for (const contactDoc of contactsSnapshot.docs) {
        const contactData = contactDoc.data();

        // Get user email for the contact
        const userDoc = await getDoc(doc(db, 'users_invited_by_users', contactData.userId));
        const userEmail = userDoc.exists() ? userDoc.data().email : 'Unknown';

        contacts.push({
          id: contactDoc.id,
          ...contactData,
          userEmail
        } as AdminContact);
      }

      return contacts;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  }

  // Get overall system metrics for admin dashboard
  static async getSystemMetrics(timeRange: 'today' | '7d' | '30d' | '90d' | 'all' = '30d'): Promise<SystemMetrics> {
    const now = new Date();

    // Resolve the window start for email/engagement stats based on timeRange
    const windowStart = (() => {
      if (timeRange === 'today') { const d = new Date(now); d.setHours(0,0,0,0); return d; }
      if (timeRange === '7d')  return new Date(now.getTime() - 7  * 86400000);
      if (timeRange === '30d') return new Date(now.getTime() - 30 * 86400000);
      if (timeRange === '90d') return new Date(now.getTime() - 90 * 86400000);
      return null; // 'all' — no lower bound
    })();

    const thirtyDaysAgo = Timestamp.fromDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
    const thisMonth = Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth(), 1));
    const lastMonth = Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));

    // Initialize all metrics with safe defaults
    let totalUsers = 0;
    let activeUsers = 0;
    let newUsersThisMonth = 0;
    let newUsersLastMonth = 0;
    let totalRevenue = 0;
    let revenueThisMonth = 0;
    let revenueLastMonth = 0;
    let revenueLast3Months = 0;
    let revenueLast6Months = 0;
    let revenueThisYear = 0;
    let monthlyRecurringRevenue = 0;
    let paidUsers = 0;
    let totalCampaigns = 0;
    let totalContacts = 0;
    let recentTransactions: TransactionRecord[] = [];
    let revenuePerPlan = { starter: 0, pro: 0, business: 0, enterprise: 0 };
    let revenueByBillingCycle = { monthly: 0, yearly: 0 };
    let revenueByPaymentMethod = { card: 0, paypal: 0, stripe: 0 };
    let revenueByGeography: Record<string, number> = { 'Global': 0 };
    let emailsSentToday = 0;
    let emailsSentThisMonth = 0;
    let emailsSentLastMonth = 0;
    let totalEmailsSent = 0;
    let emailOpenRate = 0;
    let emailClickRate = 0;
    let bounceRate = 0;
    let unsubscribeRateComputed = 0;
    let cancelledCount = 0;
    let churnRate = 0;

    // 1. User Metrics (Resilient)
    try {
      const usersCol = collection(db, 'users');
      const [totalUsersCount, activeUsersCount, newUsersThisMonthCount] = await Promise.all([
        getCountFromServer(usersCol),
        getCountFromServer(query(usersCol, where('status', '==', 'active'))),
        getCountFromServer(query(usersCol, where('createdAt', '>=', thisMonth)))
      ]);

      totalUsers = totalUsersCount.data().count;
      activeUsers = activeUsersCount.data().count;
      newUsersThisMonth = newUsersThisMonthCount.data().count;

      const newUsersLastMonthQuery = query(
        usersCol,
        where('createdAt', '>=', lastMonth),
        where('createdAt', '<', thisMonth)
      );
      const lastMonthCount = await getCountFromServer(newUsersLastMonthQuery);
      newUsersLastMonth = lastMonthCount.data().count;
    } catch (error) {
      console.warn('⚠️ User metrics failed:', error);
    }

    // 2. Revenue & Transactions (Resilient)
    try {
      const paymentsCol = collection(db, 'payments');
      const successfulPaymentsQuery = query(
        paymentsCol,
        where('status', 'in', ['captured', 'authorized', 'paid'])
      );

      // Total & Breakdown
      try {
        const totalAgg = await getAggregateFromServer(successfulPaymentsQuery, { total: sum('amount') });
        totalRevenue = (totalAgg.data().total || 0) / 100;
        revenueByGeography['Global'] = totalRevenue;
      } catch (e) { console.warn('⚠️ Total rev failed:', e); }

      // Range Aggregations
      const periods = [
        { key: '3m', date: Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth() - 2, 1)) },
        { key: '6m', date: Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth() - 5, 1)) },
        { key: 'year', date: Timestamp.fromDate(new Date(now.getFullYear(), 0, 1)) },
        { key: 'cur', date: thisMonth },
        { key: 'last', date: lastMonth, end: thisMonth }
      ];

      for (const p of periods) {
        try {
          const q = p.end 
            ? query(successfulPaymentsQuery, where('createdAt', '>=', p.date), where('createdAt', '<', p.end))
            : query(successfulPaymentsQuery, where('createdAt', '>=', p.date));
          const agg = await getAggregateFromServer(q, { s: sum('amount') });
          const val = (agg.data().s || 0) / 100;
          if (p.key === '3m') revenueLast3Months = val;
          else if (p.key === '6m') revenueLast6Months = val;
          else if (p.key === 'year') revenueThisYear = val;
          else if (p.key === 'cur') revenueThisMonth = val;
          else if (p.key === 'last') revenueLastMonth = val;
        } catch (e) { console.warn(`⚠️ Rev agg for ${p.key} failed:`, e); }
      }

      // Sampling
      try {
        const breakdownSnap = await getDocs(query(successfulPaymentsQuery, limit(30)));
        if (!breakdownSnap.empty) {
          revenueByGeography = {};
          for (const snap of breakdownSnap.docs) {
            const d = snap.data();
            const amt = (d.amount || 0) / 100;
            const met = (d.method || 'card').toLowerCase();
            if (met.includes('paypal')) revenueByPaymentMethod.paypal += amt;
            else if (met.includes('stripe')) revenueByPaymentMethod.stripe += amt;
            else revenueByPaymentMethod.card += amt;

            // Try to get real country from user profile if possible, otherwise use 'Global'
            let country = 'Global';
            if (d.userId) {
              try {
                const uDoc = await getDoc(doc(db, 'users', d.userId));
                if (uDoc.exists()) {
                  const uData = uDoc.data();
                  country = uData.deviceInfo?.country || uData.country || 'Global';
                }
              } catch (e) { /* ignore */ }
            }
            revenueByGeography[country] = (revenueByGeography[country] || 0) + amt;
          }
        }
      } catch (e) { console.warn('⚠️ Rev breakdown failed:', e); }

      // Recent Transactions (from Invoices)
      try {
        const invoicesCol = collection(db, 'invoices');
        const recentInvoicesSnap = await getDocs(query(invoicesCol, orderBy('created', 'desc'), limit(15)));
        
        const txnResults: TransactionRecord[] = [];
        const userCache = new Map<string, string>();

        for (const snap of recentInvoicesSnap.docs) {
          const inv = snap.data();
          const userId = inv.userId || inv.customer;
          
          // Skip dummy test data
          if (inv.customer?.includes('example.com') || inv.userId?.includes('example.com') || inv.customer?.includes('test.com')) {
            continue;
          }

          let customerName = inv.customer || inv.userId || 'Customer';
          
          if (userId && !customerName.includes('@')) {
            if (userCache.has(userId)) {
              customerName = userCache.get(userId)!;
            } else {
              try {
                const uDoc = await getDoc(doc(db, 'users', userId));
                if (uDoc.exists()) {
                  const uData = uDoc.data();
                  customerName = uData.email || uData.displayName || customerName;
                  userCache.set(userId, customerName);
                }
              } catch (e) { /* ignore */ }
            }
          }

          // Final filter to ensure no example emails leak through from metadata
          if (customerName.toLowerCase().includes('example.com')) continue;

          txnResults.push({
            id: snap.id,
            customerName: customerName,
            amount: (inv.amount_paid || 0) / 100,
            currency: (inv.currency || 'USD').toUpperCase(),
            tax: 0,
            status: inv.status === 'paid' ? 'paid' : inv.status === 'failed' ? 'failed' : 'pending',
            date: inv.created?.toDate ? inv.created.toDate() : new Date(inv.created * 1000),
            invoiceId: inv.id
          });

          if (txnResults.length >= 10) break;
        }
        recentTransactions = txnResults;
      } catch (e) { console.warn('⚠️ Recent trans failed:', e); }

    } catch (error) {
      console.warn('⚠️ General rev metrics failed:', error);
    }

    // 3. Subscriptions & Churn (Resilient)
    try {
      const usersCol = collection(db, 'users');
      // Fetch users with active subscriptions to calculate MRR and plan breakdown
      const activeUsersSnap = await getDocs(query(usersCol, where('subscription.status', '==', 'active')));
      
      let totalMrr = 0;
      let pUsersCount = 0;
      
      activeUsersSnap.docs.forEach(snap => {
        const userData = snap.data() as any;
        const sub = userData.subscription;
        if (!sub || sub.plan === 'starter' || sub.plan === 'free') return; 

        pUsersCount++;
        const subscribers = sub.subscribers || 0;
        const amt = this.calculateMonthlyPrice(subscribers, sub.currency || 'usd', sub.plan);
        const interval = sub.billingPeriod || 'monthly';
        
        totalMrr += interval === 'annual' ? amt / 12 : amt;

        const plan = (sub.plan || 'starter').toLowerCase();
        if (plan.includes('pro')) revenuePerPlan.pro += amt;
        else if (plan.includes('business')) revenuePerPlan.business += amt;
        else if (plan.includes('enterprise')) revenuePerPlan.enterprise += amt;
        else revenuePerPlan.starter += amt;

        if (interval === 'annual') revenueByBillingCycle.yearly += amt;
        else revenueByBillingCycle.monthly += amt;
      });

      paidUsers = pUsersCount;
      monthlyRecurringRevenue = totalMrr;

      try {
        // SIMPLIFIED: Removing 'updatedAt' constraint to avoid Index Error while permanent indexes are built
        const churnQuery = query(usersCol, where('subscription.status', 'in', ['cancelled', 'inactive']));
        const churnAgg = await getCountFromServer(churnQuery);
        cancelledCount = churnAgg.data().count;
        churnRate = paidUsers > 0 ? (cancelledCount / (paidUsers + cancelledCount)) * 100 : 0;
      } catch (e) {
        console.warn('⚠️ Churn agg failed (likely missing index):', e);
      }
    } catch (e) { console.warn('⚠️ Sub metrics failed:', e); }

    // 4. Activity & Engagement (Resilient)
    try {
      const [cCount, tCount] = await Promise.all([
        getCountFromServer(collection(db, 'campaigns')),
        getCountFromServer(collection(db, 'contacts'))
      ]);
      totalCampaigns = cCount.data().count;
      totalContacts = tCount.data().count;

      const sentCampaigns = await getDocs(
        windowStart
          ? query(collection(db, 'campaigns'), where('status', '==', 'sent'), where('sentAt', '>=', Timestamp.fromDate(windowStart)))
          : query(collection(db, 'campaigns'), where('status', '==', 'sent'))
      );
      let tOpens = 0, tClicks = 0, tBounces = 0, tUnsubscribes = 0, tRecipientsForStats = 0;
      const startOfDay = new Date(); startOfDay.setHours(0,0,0,0);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);

      sentCampaigns.docs.forEach(snap => {
        const d = snap.data();
        const sentAt = d.sentAt?.toDate();
        const rec = d.statistics?.totalRecipients || d.recipientCount || 0;
        totalEmailsSent += rec;

        if (sentAt && sentAt >= startOfDay) emailsSentToday += rec;
        if (sentAt && sentAt >= thisMonth.toDate()) emailsSentThisMonth += rec;
        if (sentAt && sentAt >= lastMonthStart && sentAt < lastMonthEnd) emailsSentLastMonth += rec;

        if (d.statistics) {
          tOpens += d.statistics.opens || 0;
          tClicks += d.statistics.clicks || 0;
          tBounces += d.statistics.bounces || 0;
          tUnsubscribes += d.statistics.unsubscribes || d.statistics.optOuts || 0;
          tRecipientsForStats += rec;
        }
      });

      emailOpenRate = tRecipientsForStats > 0 ? (tOpens / tRecipientsForStats) * 100 : 0;
      emailClickRate = tRecipientsForStats > 0 ? (tClicks / tRecipientsForStats) * 100 : 0;
      bounceRate = tRecipientsForStats > 0 ? (tBounces / tRecipientsForStats) * 100 : 0;
      const unsubscribeRateComputed = tRecipientsForStats > 0 ? (tUnsubscribes / tRecipientsForStats) * 100 : 0;
    } catch (e) { console.warn('⚠️ Activity metrics failed:', e); }

    const growthRate = newUsersLastMonth > 0 ? ((newUsersThisMonth - newUsersLastMonth) / newUsersLastMonth) * 100 : 0;

    return {
      totalUsers, activeUsers, totalCampaigns, totalContacts, emailsSentToday, emailsSentThisMonth,
      systemUptime: 0, averageResponseTime: 0, paidUsers, // not measured — removed hardcoded values
      unpaidUsers: 0, // users with past-due/failed payments — not tracked separately yet
      freeUsers: Math.max(0, totalUsers - paidUsers), // starter/free plan users
      premiumUsers: paidUsers,
      revenueThisMonth: Math.round(revenueThisMonth),
      revenueLastMonth: Math.round(revenueLastMonth),
      revenueLast3Months: Math.round(revenueLast3Months),
      revenueLast6Months: Math.round(revenueLast6Months),
      revenueThisYear: Math.round(revenueThisYear),
      monthlyRecurringRevenue: Math.round(monthlyRecurringRevenue),
      averageRevenuePerUser: paidUsers > 0 ? monthlyRecurringRevenue / paidUsers : 0,
      newUsersThisMonth, newUsersLastMonth,
      userGrowthRate: Math.round(growthRate * 10) / 10,
      churnRate: Math.round(churnRate * 10) / 10,
      retentionRate: Math.round((100 - churnRate) * 10) / 10,
      averageContactsPerUser: totalUsers > 0 ? totalContacts / totalUsers : 0,
      averageCampaignsPerUser: totalUsers > 0 ? totalCampaigns / totalUsers : 0,
      emailOpenRate: Math.round(emailOpenRate * 10) / 10,
      emailClickRate: Math.round(emailClickRate * 10) / 10,
      unsubscribeRate: Math.round(unsubscribeRateComputed * 100) / 100,
      totalEmailsSent, emailsSentLastMonth, averageEmailDeliveryTime: 0, // delivery time not measured
      bounceRate: Math.round(bounceRate * 10) / 10,
      conversionRate: totalUsers > 0 ? (paidUsers / totalUsers) * 100 : 0,
      customerLifetimeValue: paidUsers > 0 ? totalRevenue / paidUsers : 0,
      totalRevenue: Math.round(totalRevenue),
      monthlyGrowthRate: Math.round(growthRate * 10) / 10,
      annualRecurringRevenue: Math.round(monthlyRecurringRevenue * 12),
      netRevenue: 0, // Not computed — requires real payment processor data
      revenuePerPlan, revenueByBillingCycle, revenueByPaymentMethod, revenueByGeography,
      newSubscriptionsThisMonth: 0, // Not computed — requires subscription event tracking
      cancelledSubscriptionsThisMonth: cancelledCount,
      trialToPaidConversionRate: 0, // Not computed — requires trial tracking
      renewalRate: Math.round((100 - churnRate) * 10) / 10,
      expansionRevenue: 0, // Not computed — requires subscription change tracking
      contractionRevenue: 0, // Not computed — requires subscription change tracking
      revenueForecastNext30Days: Math.round(monthlyRecurringRevenue), // Conservative: same as MRR
      recentTransactions
    };
  }

  // Helper method to calculate monthly price based on subscribers, currency and plan
  // Uses the same pricing tiers as the live pricing page — single source of truth.
  private static calculateMonthlyPrice(subscribers: number, _currency: string, plan: string = 'pro'): number {
    const p = (plan || '').toLowerCase();
    if (p === 'starter' || p === 'free') return 0;
    if (p === 'business') return BUSINESS_MONTHLY_PRICE; // flat $249/mo
    if (p === 'enterprise') return BUSINESS_MONTHLY_PRICE * 2; // rough enterprise estimate
    // Pro: tiered by subscriber count using real pricing
    return getProMonthlyPrice(subscribers);
  }

  private static asTimestamp(v: unknown, fallback: Timestamp): Timestamp {
    if (v && typeof v === 'object' && 'toDate' in v && typeof (v as Timestamp).toDate === 'function') {
      return v as Timestamp;
    }
    return fallback;
  }

  /** Maps a `users` document to a paid subscription row, or `null` for starter/free/missing sub. */
  private static mapUserDocToAdminSubscription(
    snap: QueryDocumentSnapshot<DocumentData>
  ): AdminSubscription | null {
    const userData = snap.data() as Record<string, unknown>;
    const sub = userData.subscription as Record<string, unknown> | undefined;
    if (!sub) return null;

    const planId = String(sub.plan || 'starter').toLowerCase();
    if (planId === 'starter' || planId === 'free') return null;

    const createdFallback = this.asTimestamp(userData.createdAt, Timestamp.now());
    const subscribers = Number(sub.subscribers) || 0;
    const amount = this.calculateMonthlyPrice(
      subscribers,
      String(sub.currency || 'usd'),
      String(sub.plan || 'pro')
    );
    const interval: 'monthly' | 'yearly' = sub.billingPeriod === 'annual' ? 'yearly' : 'monthly';

    const rawStatus = String(sub.status || 'active').toLowerCase();
    let status: AdminSubscription['status'] = 'active';
    if (rawStatus === 'cancelled' || rawStatus === 'canceled') status = 'cancelled';
    else if (rawStatus === 'past_due') status = 'past_due';
    else if (rawStatus === 'unpaid') status = 'unpaid';
    else if (rawStatus === 'expired') status = 'expired';

    const currentStart = this.asTimestamp(
      sub.currentPeriodStart || sub.startDate || userData.createdAt,
      createdFallback
    );
    const currentEnd = this.asTimestamp(
      sub.currentPeriodEnd || sub.startDate || userData.createdAt,
      createdFallback
    );
    const createdAt = this.asTimestamp(sub.startDate || userData.createdAt, createdFallback);

    const paymentProvider =
      typeof sub.paymentProvider === 'string' ? sub.paymentProvider : undefined;
    const lastPaymentReference =
      typeof sub.lastPaymentReference === 'string' ? sub.lastPaymentReference : undefined;
    const dodoSubscriptionId =
      typeof sub.dodoSubscriptionId === 'string' ? sub.dodoSubscriptionId : undefined;
    const dodoPaymentId = typeof sub.dodoPaymentId === 'string' ? sub.dodoPaymentId : undefined;
    const stripeSubscriptionId =
      typeof sub.stripeSubscriptionId === 'string' ? sub.stripeSubscriptionId : undefined;

    return {
      id: snap.id,
      userId: snap.id,
      planId: String(sub.plan || 'pro'),
      status,
      currentStart,
      currentEnd,
      createdAt,
      amount: amount * 100,
      currency: (String(sub.currency || 'USD') || 'USD').toUpperCase(),
      interval,
      paymentMethod: typeof sub.paymentMethod === 'string' ? sub.paymentMethod : undefined,
      userEmail: (typeof userData.email === 'string' && userData.email) || 'No email',
      paymentProvider,
      lastPaymentReference,
      dodoSubscriptionId,
      dodoPaymentId,
      stripeSubscriptionId,
      cancelAtPeriodEnd: !!sub.cancelAtPeriodEnd,
      subscribersCap: subscribers,
      emailsPerMonth:
        typeof sub.emailsPerMonth === 'number'
          ? sub.emailsPerMonth
          : typeof sub.emails === 'number'
            ? sub.emails
            : undefined,
      displayName: typeof userData.displayName === 'string' ? userData.displayName : undefined,
      companyName: typeof userData.companyName === 'string' ? userData.companyName : undefined
    };
  }

  /**
   * Values for `where('subscription.plan', 'in', …)` (Firestore `in` max 10). Lowercase matches Dodo/checkout;
   * title case covers older or hand-edited docs.
   */
  private static readonly PAID_SUBSCRIPTION_PLAN_IN_VALUES = [
    'pro',
    'Pro',
    'business',
    'Business',
    'enterprise',
    'Enterprise'
  ] as const;

  private static readonly PAID_SUBSCRIPTION_PLAN_SET = new Set(
    this.PAID_SUBSCRIPTION_PLAN_IN_VALUES.map((v) => String(v).toLowerCase())
  );

  private static isPaidPlanValue(plan: unknown): boolean {
    return this.PAID_SUBSCRIPTION_PLAN_SET.has(String(plan || '').toLowerCase());
  }

  /**
   * Paginated `users` with an active paid subscription (status active + plan pro/business/enterprise).
   * Matches rows written by Dodo (Cloud Functions), Razorpay/Stripe (in-app `subscriptionService`), etc.
   */
  static async getSubscriptionsPage(
    pageSize: number = 100,
    startAfterDoc?: QueryDocumentSnapshot<DocumentData> | null
  ): Promise<AdminSubscriptionsPageResult> {
    try {
      const usersCol = collection(db, 'users');
      const paidPlans = [...this.PAID_SUBSCRIPTION_PLAN_IN_VALUES];
      let usersSnap;
      try {
        // Primary path: surface newest paid subscriptions first.
        usersSnap = await getDocs(
          startAfterDoc
            ? query(
                usersCol,
                where('subscription.status', '==', 'active'),
                where('subscription.plan', 'in', paidPlans),
                orderBy('subscription.updatedAt', 'desc'),
                orderBy(documentId()),
                startAfter(startAfterDoc),
                limit(pageSize)
              )
            : query(
                usersCol,
                where('subscription.status', '==', 'active'),
                where('subscription.plan', 'in', paidPlans),
                orderBy('subscription.updatedAt', 'desc'),
                orderBy(documentId()),
                limit(pageSize)
              )
        );
      } catch (primaryErr) {
        console.warn('getSubscriptionsPage: updatedAt-ordered query failed, falling back to documentId order', primaryErr);
        try {
          // Backward-compatible fallback for projects missing the updatedAt composite index.
          usersSnap = await getDocs(
            startAfterDoc
              ? query(
                  usersCol,
                  where('subscription.status', '==', 'active'),
                  where('subscription.plan', 'in', paidPlans),
                  orderBy(documentId()),
                  startAfter(startAfterDoc),
                  limit(pageSize)
                )
              : query(
                  usersCol,
                  where('subscription.status', '==', 'active'),
                  where('subscription.plan', 'in', paidPlans),
                  orderBy(documentId()),
                  limit(pageSize)
                )
          );
        } catch (fallbackErr) {
          console.warn('getSubscriptionsPage: paid-plan query failed, using status-only fallback', fallbackErr);
          // Last-resort fallback: query by status only, then filter paid plans in memory.
          usersSnap = await getDocs(
            startAfterDoc
              ? query(
                  usersCol,
                  where('subscription.status', '==', 'active'),
                  orderBy(documentId()),
                  startAfter(startAfterDoc),
                  limit(pageSize)
                )
              : query(
                  usersCol,
                  where('subscription.status', '==', 'active'),
                  orderBy(documentId()),
                  limit(pageSize)
                )
          );
        }
      }

      const docs = usersSnap.docs;
      const lastDoc = docs.length > 0 ? docs[docs.length - 1]! : null;
      const hasMore = docs.length === pageSize;

      const subscriptions = docs
        .filter((d) => {
          const userData = d.data() as Record<string, unknown>;
          const subscription =
            userData.subscription && typeof userData.subscription === 'object'
              ? (userData.subscription as Record<string, unknown>)
              : null;
          const plan = subscription?.plan;
          return this.isPaidPlanValue(plan);
        })
        .map(d => this.mapUserDocToAdminSubscription(d))
        .filter((row): row is AdminSubscription => row != null);

      return {
        subscriptions,
        nextCursor: lastDoc,
        hasMore
      };
    } catch (error) {
      console.error('Error fetching subscriptions page:', error);
      return { subscriptions: [], nextCursor: null, hasMore: false };
    }
  }

  /**
   * Latest paid `invoices` row per user. Backfills gateway when `users.subscription` lost `paymentProvider`
   * (e.g. after a client overwrite). Dodo invoices store `subscriptionId` like `dodo_<paymentId>` from Cloud Functions.
   */
  static async getLatestPaymentGatewayHintsByUserId(
    maxInvoices = 500
  ): Promise<Record<string, { provider: string; externalId: string }>> {
    try {
      const { billingService } = await import('./billingService');
      const invoices = await billingService.getAllInvoices(maxInvoices);
      const best = new Map<string, { created: number; provider: string; externalId: string }>();

      const inferFromPaymentId = (pid: string): string => {
        if (pid.startsWith('pay_')) return 'razorpay';
        if (pid.startsWith('pi_')) return 'stripe';
        if (pid.startsWith('dodo_')) return 'dodo';
        return 'unknown';
      };

      for (const inv of invoices) {
        const uid = (inv.userId || inv.customer || '') as string;
        if (!uid || typeof uid !== 'string') continue;
        const created = typeof inv.created === 'number' ? inv.created : 0;
        const prev = best.get(uid);
        if (prev && prev.created >= created) continue;

        const subId = inv.subscriptionId ? String(inv.subscriptionId) : '';
        const pid = inv.paymentId ? String(inv.paymentId) : '';

        let provider = 'unknown';
        let externalId = '';

        if (subId.startsWith('dodo_')) {
          provider = 'dodo';
          externalId = subId;
        } else if (pid) {
          provider = inferFromPaymentId(pid);
          externalId = pid;
        } else {
          continue;
        }

        if (provider === 'unknown' && !externalId) continue;

        best.set(uid, { created, provider, externalId });
      }

      const out: Record<string, { provider: string; externalId: string }> = {};
      best.forEach((v, uid) => {
        out[uid] = { provider: v.provider, externalId: v.externalId };
      });
      return out;
    } catch (e) {
      console.error('getLatestPaymentGatewayHintsByUserId:', e);
      return {};
    }
  }

  /**
   * Latest invoice links per user for admin actions (view/download).
   */
  static async getLatestInvoiceLinksByUserId(
    maxInvoices = 1000
  ): Promise<Record<string, { invoiceId: string; viewUrl: string; downloadUrl: string }>> {
    try {
      const { billingService } = await import('./billingService');
      const invoices = await billingService.getAllInvoices(maxInvoices);
      const best = new Map<string, { created: number; invoiceId: string; viewUrl: string; downloadUrl: string }>();

      for (const inv of invoices) {
        const uid = (inv.userId || inv.customer || '') as string;
        if (!uid || typeof uid !== 'string') continue;
        const viewUrl = (inv.hosted_invoice_url && String(inv.hosted_invoice_url).trim()) || '';
        const downloadUrl = (inv.invoice_pdf && String(inv.invoice_pdf).trim()) || '';
        if (!viewUrl && !downloadUrl) continue;
        const created = typeof inv.created === 'number' ? inv.created : 0;
        const prev = best.get(uid);
        if (prev && prev.created >= created) continue;
        best.set(uid, {
          created,
          invoiceId: String(inv.id || ''),
          viewUrl,
          downloadUrl
        });
      }

      const out: Record<string, { invoiceId: string; viewUrl: string; downloadUrl: string }> = {};
      best.forEach((v, uid) => {
        out[uid] = {
          invoiceId: v.invoiceId,
          viewUrl: v.viewUrl,
          downloadUrl: v.downloadUrl
        };
      });
      return out;
    } catch (e) {
      console.error('getLatestInvoiceLinksByUserId:', e);
      return {};
    }
  }

  /**
   * Latest paid invoice amount per user (minor units + currency + created seconds).
   * Use this for accurate admin MRR + admin-generated invoices, especially for Dodo discounts.
   */
  static async getLatestPaidInvoiceAmountsByUserId(
    maxInvoices = 1500
  ): Promise<Record<string, { amountPaidMinor: number; currency: string; created: number }>> {
    try {
      const { billingService } = await import('./billingService');
      const invoices = await billingService.getAllInvoices(maxInvoices);
      const best = new Map<string, { created: number; amountPaidMinor: number; currency: string }>();

      for (const inv of invoices) {
        const uid = (inv.userId || inv.customer || '') as string;
        if (!uid || typeof uid !== 'string') continue;
        if (String(inv.status || '').toLowerCase() !== 'paid') continue;
        const amountPaidMinor = typeof inv.amount_paid === 'number' ? inv.amount_paid : 0;
        if (!Number.isFinite(amountPaidMinor) || amountPaidMinor < 0) continue;
        const created = typeof inv.created === 'number' ? inv.created : 0;
        const prev = best.get(uid);
        if (prev && prev.created >= created) continue;
        const currency = String((inv.currency || 'usd') ?? 'usd').toLowerCase();
        best.set(uid, { created, amountPaidMinor: Math.round(amountPaidMinor), currency });
      }

      const out: Record<string, { amountPaidMinor: number; currency: string; created: number }> = {};
      best.forEach((v, uid) => {
        out[uid] = { amountPaidMinor: v.amountPaidMinor, currency: v.currency, created: v.created };
      });
      return out;
    } catch (e) {
      console.error('getLatestPaidInvoiceAmountsByUserId:', e);
      return {};
    }
  }

  // Helper method to calculate churn rate
  private static calculateChurnRate(users: any[], lastMonth: Date, thisMonth: Date): number {
    // Users who were active last month but not this month
    const activeLastMonth = users.filter(user => {
      const lastLogin = user.lastLoginAt?.toDate();
      return lastLogin && lastLogin >= lastMonth && lastLogin < thisMonth;
    }).length;

    const churnedThisMonth = users.filter(user => {
      const lastLogin = user.lastLoginAt?.toDate();
      const wasActiveLastMonth = lastLogin && lastLogin >= lastMonth && lastLogin < thisMonth;
      const isActiveThisMonth = lastLogin && lastLogin >= thisMonth;
      return wasActiveLastMonth && !isActiveThisMonth;
    }).length;

    return activeLastMonth > 0 ? (churnedThisMonth / activeLastMonth) * 100 : 0;
  }

  /** Convert stored payment amounts to major currency units (e.g. dollars). */
  static paymentAmountToMajor(amount: number, method?: string): number {
    if (!Number.isFinite(amount)) return 0;
    const m = String(method || '').toLowerCase();
    if (m === 'dodo' || m === 'stripe') return amount / 100;
    if (amount >= 10000 && Number.isInteger(amount)) return amount / 100;
    return amount;
  }

  static sumCapturedPaymentsLast30Days(payments: AdminPayment[]): number {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return payments
      .filter((p) => {
        const status = String(p.status || '').toLowerCase();
        if (!['captured', 'paid', 'succeeded', 'success'].includes(status)) return false;
        const t = p.capturedAt?.toDate?.() || p.createdAt?.toDate?.();
        return Boolean(t && t.getTime() >= cutoff);
      })
      .reduce((sum, p) => sum + this.paymentAmountToMajor(p.amount, p.method), 0);
  }

  // Get user contacts for admin view (userId + all workspace contacts the user owns OR is a member of)
  static async getUserContacts(userId: string): Promise<AdminContact[]> {
    try {
      const byId = new Map<string, AdminContact>();

      // 1. Contacts stamped with this userId in the root collection
      const byUserSnap = await getDocs(
        query(collection(db, 'contacts'), where('userId', '==', userId))
      );
      byUserSnap.docs.forEach((docSnap) => {
        byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as AdminContact);
      });

      // 2. Workspaces where user is the creator
      const createdWorkspacesSnap = await getDocs(
        query(collection(db, 'workspaces'), where('createdBy', '==', userId))
      );

      // 3. Workspaces where user is a member (non-owner collaborator)
      const memberSnap = await getDocs(
        query(collection(db, 'workspace_members'), where('userId', '==', userId), where('isActive', '==', true))
      );
      const memberWorkspaceIds = new Set<string>(memberSnap.docs.map(d => d.data().workspaceId).filter(Boolean));

      // Combine owned + member workspace IDs, deduplicated
      const allWorkspaceIds = new Set<string>(createdWorkspacesSnap.docs.map(d => d.id));
      memberWorkspaceIds.forEach(id => allWorkspaceIds.add(id));

      for (const wsId of Array.from(allWorkspaceIds)) {
        const wsSnap = await getDocs(
          query(collection(db, 'contacts'), where('workspaceId', '==', wsId))
        );
        wsSnap.docs.forEach((docSnap) => {
          if (!byId.has(docSnap.id)) {
            byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as AdminContact);
          }
        });
      }

      return Array.from(byId.values());
    } catch (error) {
      console.error('Error fetching user contacts:', error);
      throw error;
    }
  }

  static async getUserExportHistory(userId: string, limitCount = 10): Promise<Record<string, unknown>[]> {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'export_history'),
          where('userId', '==', userId),
          orderBy('exportedAt', 'desc'),
          limit(limitCount)
        )
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Record<string, unknown>));
    } catch {
      const snap = await getDocs(query(collection(db, 'export_history'), where('userId', '==', userId)));
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as Record<string, unknown>))
        .sort((a, b) => {
          const tA = (a.exportedAt as Timestamp)?.toDate?.()?.getTime() || 0;
          const tB = (b.exportedAt as Timestamp)?.toDate?.()?.getTime() || 0;
          return tB - tA;
        })
        .slice(0, limitCount);
    }
  }

  static async getUserImportHistory(userId: string, limitCount = 10): Promise<Record<string, unknown>[]> {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'import_history'),
          where('userId', '==', userId),
          orderBy('importedAt', 'desc'),
          limit(limitCount)
        )
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Record<string, unknown>));
    } catch {
      const snap = await getDocs(query(collection(db, 'import_history'), where('userId', '==', userId)));
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as Record<string, unknown>))
        .sort((a, b) => {
          const tA = (a.importedAt as Timestamp)?.toDate?.()?.getTime() || 0;
          const tB = (b.importedAt as Timestamp)?.toDate?.()?.getTime() || 0;
          return tB - tA;
        })
        .slice(0, limitCount);
    }
  }

  static async getUserSupplementalDetails(userId: string): Promise<{
    apiKeyCount: number;
    webhookCount: number;
    storageUsedBytes: number;
    storageLimitBytes: number;
    integrationNames: string[];
    monthlyRevenueLast30Days: number;
  }> {
    const [apiKeysSnap, quotasSnap, payments] = await Promise.all([
      // Only count active (non-revoked) personal tokens
      getDocs(query(collection(db, 'oauthTokens'), where('userId', '==', userId), where('type', '==', 'personal'), where('isActive', '==', true))),
      getDocs(query(collection(db, 'storageQuotas'), where('userId', '==', userId))),
      this.getUserPaymentHistory(userId).catch(() => [] as AdminPayment[]),
    ]);

    let webhooksSnap: Awaited<ReturnType<typeof getDocs>> | null = null;
    let integrationsSnap: Awaited<ReturnType<typeof getDocs>> | null = null;
    try {
      webhooksSnap = await getDocs(query(collection(db, 'zapierWebhooks'), where('userId', '==', userId)));
    } catch {
      webhooksSnap = null;
    }
    try {
      integrationsSnap = await getDocs(query(collection(db, 'userIntegrations'), where('userId', '==', userId)));
    } catch {
      integrationsSnap = null;
    }

    let storageUsedBytes = 0;
    let storageLimitBytes = 0;
    quotasSnap.docs.forEach((docSnap) => {
      const data = docSnap.data();
      storageUsedBytes += typeof data.usedStorage === 'number' ? data.usedStorage : 0;
      storageLimitBytes += typeof data.totalStorage === 'number' ? data.totalStorage : 0;
    });

    if (storageLimitBytes === 0) {
      const filesSnap = await getDocs(query(collection(db, 'storageFiles'), where('userId', '==', userId)));
      storageUsedBytes = filesSnap.docs.reduce((sum, d) => {
        const size = d.data().size;
        return sum + (typeof size === 'number' ? size : 0);
      }, 0);
    }

    const integrationNames = (integrationsSnap?.docs || [])
      .map((d) => {
        const data = d.data() as Record<string, unknown>;
        return (
          (typeof data.name === 'string' && data.name) ||
          (typeof data.provider === 'string' && data.provider) ||
          (typeof data.slug === 'string' && data.slug) ||
          ''
        );
      })
      .filter(Boolean) as string[];

    return {
      apiKeyCount: apiKeysSnap.size,
      webhookCount: webhooksSnap?.size ?? 0,
      storageUsedBytes,
      storageLimitBytes,
      integrationNames,
      monthlyRevenueLast30Days: this.sumCapturedPaymentsLast30Days(payments),
    };
  }

  // Get user-specific metrics — campaign statistics + API email usage + contact breakdown
  static async getUserMetrics(userId: string): Promise<{
    totalCampaigns: number;
    sentCampaigns: number;
    draftCampaigns: number;
    scheduledCampaigns: number;
    totalEmailsSent: number;
    totalContacts: number;
    activeContacts: number;
    unsubscribedContacts: number;
    bouncedContacts: number;
    lastCampaignDate: Date | null;
    subscriptionStatus: string;
    subscriptionPlan: string;
    monthlyRevenue: number;
    currentMonthEmailUsage: number;
  }> {
    try {
      // Current calendar month start
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthStartTs = Timestamp.fromDate(monthStart);

      const [campaignsSnapshot, contactsSnapshot, userDoc, apiSendsSnap] = await Promise.all([
        getDocs(query(collection(db, 'campaigns'), where('userId', '==', userId))),
        getDocs(query(collection(db, 'contacts'), where('userId', '==', userId))),
        getDoc(doc(db, 'users', userId)),
        // API / one-off sends not tied to a campaign (sentAt within current month)
        getDocs(query(
          collection(db, 'email_logs'),
          where('userId', '==', userId),
          where('status', '==', 'sent'),
          where('sentAt', '>=', monthStartTs)
        )).catch(() => null),
      ]);

      const campaigns = campaignsSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));
      const contacts  = contactsSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));

      const userData     = userDoc.exists() ? userDoc.data() : null;
      const subscription = userData?.subscription;

      // Campaign breakdowns
      const sentCampaigns      = campaigns.filter((c: any) => c.status === 'sent').length;
      const draftCampaigns     = campaigns.filter((c: any) => c.status === 'draft').length;
      const scheduledCampaigns = campaigns.filter((c: any) => c.status === 'scheduled').length;

      // Total emails sent from campaign statistics (all-time)
      const campaignEmailsSent = campaigns.reduce((sum: number, c: any) => sum + (c.statistics?.sent || 0), 0);

      // API / one-off sends this month (exclude campaign-tagged logs to avoid double-count)
      const apiEmailsSentThisMonth = (apiSendsSnap?.docs || []).filter((d: any) => {
        const data = d.data();
        return !data.campaignId && data.isSystemEmail !== true;
      }).length;

      // Combined total emails sent
      const totalEmailsSent = campaignEmailsSent + apiEmailsSentThisMonth;

      // Current month usage: campaign sends this month + API sends this month
      const campaignSendsThisMonth = campaigns
        .filter((c: any) => { const s = c.sentAt?.toDate?.(); return s && s >= monthStart; })
        .reduce((sum: number, c: any) => sum + (c.statistics?.sent || 0), 0);
      const currentMonthEmailUsage = campaignSendsThisMonth + apiEmailsSentThisMonth;

      const lastCampaignDate = campaigns.length > 0
        ? campaigns
            .filter((c: any) => c.sentAt)
            .sort((a: any, b: any) => (b.sentAt?.toDate?.()?.getTime?.() || 0) - (a.sentAt?.toDate?.()?.getTime?.() || 0))[0]
            ?.sentAt?.toDate?.() || null
        : null;

      const payments = await this.getUserPaymentHistory(userId).catch(() => [] as AdminPayment[]);

      return {
        totalCampaigns: campaigns.length,
        sentCampaigns,
        draftCampaigns,
        scheduledCampaigns,
        totalEmailsSent,
        totalContacts:        contacts.length,
        activeContacts:       contacts.filter((c: any) => c.status === 'subscribed').length,
        unsubscribedContacts: contacts.filter((c: any) => c.status === 'unsubscribed').length,
        bouncedContacts:      contacts.filter((c: any) => c.status === 'bounced').length,
        lastCampaignDate,
        subscriptionStatus: subscription?.status || 'inactive',
        subscriptionPlan:   subscription?.plan   || 'starter',
        monthlyRevenue: this.sumCapturedPaymentsLast30Days(payments),
        currentMonthEmailUsage,
      };
    } catch (error) {
      console.error('Error fetching user metrics:', error);
      throw error;
    }
  }

  // Subscription Management — walks all pages so revenue views are not capped at 100.
  static async getAllSubscriptions(): Promise<AdminSubscription[]> {
    try {
      const subscriptions: AdminSubscription[] = [];
      let cursor: QueryDocumentSnapshot<DocumentData> | null | undefined;
      const pageSize = 200;
      const maxPages = 100;
      for (let p = 0; p < maxPages; p++) {
        const { subscriptions: chunk, nextCursor, hasMore } = await this.getSubscriptionsPage(pageSize, cursor);
        subscriptions.push(...chunk);
        if (!hasMore || !nextCursor) break;
        cursor = nextCursor;
      }

      return subscriptions.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error fetching all subscriptions from users:', error);
      return [];
    }
  }

  // Recent Activity
  static async getRecentActivity(limitCount: number = 10): Promise<any[]> {
    try {
      // This would typically come from an activity log collection
      // For now, we'll return mock data
      return [
        {
          id: '1',
          type: 'user_signup',
          description: 'New user registered',
          timestamp: Timestamp.now(),
          userId: 'user1',
          userEmail: 'newuser@example.com'
        },
        {
          id: '2',
          type: 'campaign_created',
          description: 'Campaign "Welcome Series" created',
          timestamp: Timestamp.now(),
          userId: 'user2',
          userEmail: 'user2@example.com'
        }
      ];
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  }

  // System Alerts
  static async getSystemAlerts(): Promise<any[]> {
    try {
      // This would typically come from a system alerts collection
      // For now, we'll return mock data
      return [
        {
          id: '1',
          type: 'info',
          title: 'System Update',
          message: 'Database maintenance completed successfully',
          timestamp: Timestamp.now(),
          resolved: true
        }
      ];
    } catch (error) {
      console.error('Error fetching system alerts:', error);
      throw error;
    }
  }

  // Block User
  static async blockUser(userId: string, reason?: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'blocked',
        blockedAt: Timestamp.now(),
        blockedBy: 'admin', // In a real app, this would be the current admin's ID
        blockReason: reason || 'Suspicious activity detected'
      });

      // Log the blocking action
      await setDoc(doc(db, 'admin_actions', `${userId}_block_${Date.now()}`), {
        userId,
        action: 'block_user',
        reason: reason || 'Suspicious activity detected',
        timestamp: Timestamp.now(),
        adminId: 'admin' // In a real app, this would be the current admin's ID
      });
    } catch (error) {
      console.error('Error blocking user:', error);
      throw error;
    }
  }

  // Unblock User
  static async unblockUser(userId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'active',
        blockedAt: null,
        blockedBy: null,
        blockReason: null
      });

      // Log the unblocking action
      await setDoc(doc(db, 'admin_actions', `${userId}_unblock_${Date.now()}`), {
        userId,
        action: 'unblock_user',
        timestamp: Timestamp.now(),
        adminId: 'admin' // In a real app, this would be the current admin's ID
      });
    } catch (error) {
      console.error('Error unblocking user:', error);
      throw error;
    }
  }

  // Update User Subscription Plan
  static async updateUserSubscriptionPlan(
    userId: string,
    plan: 'starter' | 'pro' | 'business' | 'enterprise',
    status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid' = 'active',
    limits?: {
      subscribers?: number;
      emails?: number;
    }
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSettingsRef = doc(db, 'user_settings', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const existingSubscription = userDoc.data().subscription || {};
      const now = Timestamp.now();
      const defaultPeriodEnd = Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)); // 30 days from now
      const planChanged = existingSubscription.plan && existingSubscription.plan !== plan;

      const sanitizedSubscribers = typeof limits?.subscribers === 'number' && !isNaN(limits.subscribers)
        ? Math.max(0, Math.floor(limits.subscribers))
        : undefined;

      const sanitizedEmails = typeof limits?.emails === 'number' && !isNaN(limits.emails)
        ? Math.max(0, Math.floor(limits.emails))
        : undefined;

      const subscriptionData = {
        ...existingSubscription,
        plan,
        status,
        subscribers:
          sanitizedSubscribers ??
          existingSubscription.subscribers ??
          (plan === 'starter' ? 1000 : 500),
        emails:
          sanitizedEmails ??
          existingSubscription.emails ??
          (plan === 'starter'
            ? 12000          // STARTER_EMAILS — matches functions/subscriptionLimits.js
            : plan === 'business'
              ? 1000000000
              : plan === 'enterprise'
                ? 2000000000
                : 10000),
        currency: existingSubscription.currency || 'usd',
        startDate: planChanged ? now : existingSubscription.startDate || now,
        currentPeriodStart: planChanged ? now : existingSubscription.currentPeriodStart || now,
        currentPeriodEnd: planChanged ? defaultPeriodEnd : existingSubscription.currentPeriodEnd || defaultPeriodEnd,
        cancelAtPeriodEnd: existingSubscription.cancelAtPeriodEnd ?? false,
        updatedAt: now
      };

      // Update both users and user_settings collections
      await updateDoc(userRef, {
        'subscription': subscriptionData,
        'planSelected': true,
        'planSelectedAt': now,
        'updatedAt': now
      });

      // Check if user_settings exists
      const userSettingsDoc = await getDoc(userSettingsRef);
      if (userSettingsDoc.exists()) {
        await updateDoc(userSettingsRef, {
          'subscription': subscriptionData,
          'updatedAt': now
        });
      } else {
        // Create user_settings if it doesn't exist
        await setDoc(userSettingsRef, {
          userId,
          subscription: subscriptionData,
          createdAt: now,
          updatedAt: now
        });
      }

      // Log the subscription change
      await setDoc(doc(db, 'admin_actions', `${userId}_subscription_${Date.now()}`), {
        userId,
        action: 'update_subscription',
        plan,
        status,
        timestamp: now,
        adminId: 'admin' // In a real app, this would be the current admin's ID
      });

      console.log(`Successfully updated subscription for user ${userId} to ${plan} plan`);
    } catch (error) {
      console.error('Error updating user subscription:', error);
      throw error;
    }
  }

  // Toggle User Plan (Free/Starter to Pro and vice versa)
  static async toggleUserPlan(userId: string): Promise<{ newPlan: 'starter' | 'pro' }> {
    try {
      // Get current user subscription
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const currentPlan = userData.subscription?.plan || 'starter';

      // Toggle plan
      const newPlan: 'starter' | 'pro' =
        currentPlan === 'starter' || currentPlan === 'free' ? 'pro' : 'starter';

      // Update the subscription
      await this.updateUserSubscriptionPlan(userId, newPlan, 'active');

      return { newPlan };
    } catch (error) {
      console.error('Error toggling user plan:', error);
      throw error;
    }
  }

  // Get User Payment History
  static async getUserPaymentHistory(userId: string): Promise<AdminPayment[]> {
    try {
      type SourceTaggedPayment = AdminPayment & { _source: 'invoice' | 'payment' | 'dodo_tx' };
      const out: SourceTaggedPayment[] = [];
      const seen = new Set<string>();

      const normalizeKeyPart = (v: unknown): string => String(v || '').trim().toLowerCase();
      const baseCanonicalKey = (entry: {
        paymentId?: string;
        orderId?: string;
        amount?: number;
        status?: string;
        createdAt?: Timestamp;
      }): string => {
        const pid = normalizeKeyPart(entry.paymentId);
        const oid = normalizeKeyPart(entry.orderId);
        const amount = typeof entry.amount === 'number' ? Math.round(entry.amount) : 0;
        const status = normalizeKeyPart(entry.status);
        const minuteBucket = entry.createdAt?.toDate
          ? Math.floor(entry.createdAt.toDate().getTime() / 60000)
          : 0;
        return `${pid}|${oid}|${amount}|${status}|${minuteBucket}`;
      };

      // 1) Primary source: invoices (covers Dodo / Stripe / Razorpay captured checkout outcomes).
      try {
        const [invoicesByUserId, invoicesByCustomer] = await Promise.all([
          getDocs(query(collection(db, 'invoices'), where('userId', '==', userId), limit(300))),
          getDocs(query(collection(db, 'invoices'), where('customer', '==', userId), limit(300))),
        ]);
        [...invoicesByUserId.docs, ...invoicesByCustomer.docs].forEach((docSnap) => {
            const data = docSnap.data() as Record<string, unknown>;
            const paymentId =
              (typeof data.paymentId === 'string' && data.paymentId) ||
              (typeof data.payment_id === 'string' && data.payment_id) ||
              (typeof data.subscriptionId === 'string' && data.subscriptionId) ||
              docSnap.id;
            const amountPaid =
              typeof data.amount_paid === 'number'
                ? data.amount_paid
                : typeof data.amount === 'number'
                  ? data.amount
                  : 0;
            const createdAt =
              (data.createdAt as Timestamp) ||
              (data.created as Timestamp) ||
              Timestamp.now();
            const key = `${paymentId}|${amountPaid}|${String(data.status || 'unknown')}`;
            if (seen.has(key)) return;
            seen.add(key);
            out.push({
              id: docSnap.id,
              paymentId,
              amount: amountPaid,
              currency: String(data.currency || 'USD').toUpperCase(),
              status:
                String(data.status || '').toLowerCase() === 'paid'
                  ? 'captured'
                  : String(data.status || '').toLowerCase() === 'failed'
                    ? 'failed'
                    : 'authorized',
              method:
                String(paymentId).startsWith('dodo_')
                  ? 'dodo'
                  : String(paymentId).startsWith('pi_')
                    ? 'stripe'
                    : String(paymentId).startsWith('pay_')
                      ? 'razorpay'
                      : 'unknown',
              email: undefined,
              contact: undefined,
              createdAt,
              capturedAt: createdAt,
              _source: 'invoice',
            } as SourceTaggedPayment);
          });
      } catch (invoiceError) {
        console.warn('getUserPaymentHistory: invoice query failed:', invoiceError);
      }

      // 1b) Dodo transaction ledger fallback (for cases where invoice linkage is delayed/missing).
      try {
        const txSnap = await getDocs(
          query(
            collection(db, 'dodoPaymentTransactions'),
            where('firebaseUid', '==', userId),
            limit(300)
          )
        );
        txSnap.docs.forEach((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const outcome = String(data.outcomeStatus || '').toLowerCase();
          if (outcome !== 'success') return;
          const paymentId =
            (typeof data.dodoPaymentId === 'string' && data.dodoPaymentId) ||
            (typeof data.dodoSubscriptionId === 'string' && `dodo_${data.dodoSubscriptionId}`) ||
            docSnap.id;
          const amountMinor =
            typeof data.amountMinor === 'number'
              ? data.amountMinor
              : typeof data.amount === 'number'
                ? data.amount
                : 0;
          const createdAt =
            (data.createdAt as Timestamp) ||
            Timestamp.now();
          const key = `${paymentId}|${amountMinor}|captured`;
          if (seen.has(key)) return;
          seen.add(key);
          out.push({
            id: docSnap.id,
            paymentId,
            amount: amountMinor,
            currency: String(data.currency || 'USD').toUpperCase(),
            status: 'captured',
            method: 'dodo',
            email:
              (typeof data.userEmail === 'string' && data.userEmail) ||
              (typeof data.customerEmail === 'string' && data.customerEmail) ||
              undefined,
            contact: undefined,
            createdAt,
            capturedAt: createdAt,
            _source: 'dodo_tx',
          } as SourceTaggedPayment);
        });
      } catch (txError) {
        console.warn('getUserPaymentHistory: dodo transaction fallback failed:', txError);
      }

      // 2) Fallback source: payments collection by user email (legacy Razorpay path).
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        const userEmail = userDoc.exists() ? userDoc.data().email : null;
        if (userEmail) {
          const paymentsQuery = query(
            collection(db, 'payments'),
            where('email', '==', userEmail),
            orderBy('createdAt', 'desc')
          );
          const paymentsSnapshot = await getDocs(paymentsQuery);
          paymentsSnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data() as Record<string, unknown>;
            const paymentId =
              (typeof data.paymentId === 'string' && data.paymentId) ||
              (typeof data.razorpay_payment_id === 'string' && data.razorpay_payment_id) ||
              docSnap.id;
            const amount = typeof data.amount === 'number' ? data.amount : 0;
            const status =
              String(data.status || '').toLowerCase() === 'captured'
                ? 'captured'
                : String(data.status || '').toLowerCase() === 'failed'
                  ? 'failed'
                  : 'authorized';
            const key = `${paymentId}|${amount}|${status}`;
            if (seen.has(key)) return;
            seen.add(key);
            out.push({
              id: docSnap.id,
              paymentId,
              orderId:
                (typeof data.orderId === 'string' && data.orderId) ||
                (typeof data.razorpay_order_id === 'string' && data.razorpay_order_id) ||
                undefined,
              amount,
              currency: String(data.currency || 'USD').toUpperCase(),
              status,
              method:
                (typeof data.method === 'string' && data.method) ||
                (typeof data.payment_method === 'string' && data.payment_method) ||
                'Unknown',
              email: typeof data.email === 'string' ? data.email : undefined,
              contact: typeof data.contact === 'string' ? data.contact : undefined,
              createdAt: (data.createdAt as Timestamp) || Timestamp.now(),
              capturedAt: (data.capturedAt as Timestamp) || undefined,
              errorCode: typeof data.error_code === 'string' ? data.error_code : undefined,
              errorDescription:
                typeof data.error_description === 'string' ? data.error_description : undefined,
              _source: 'payment',
            } as SourceTaggedPayment);
          });
        }
      } catch (paymentsError) {
        console.warn('getUserPaymentHistory: payments query failed:', paymentsError);
      }

      // Merge duplicates across sources (invoice/payment/tx) into one canonical row.
      const sourceRank: Record<SourceTaggedPayment['_source'], number> = {
        invoice: 3,
        payment: 2,
        dodo_tx: 1,
      };
      const canonical = new Map<string, SourceTaggedPayment>();
      for (const row of out) {
        const key = baseCanonicalKey(row);
        const existing = canonical.get(key);
        if (!existing) {
          canonical.set(key, row);
          continue;
        }
        const preferExisting = sourceRank[existing._source] >= sourceRank[row._source];
        const base = preferExisting ? existing : row;
        const extra = preferExisting ? row : existing;
        canonical.set(key, {
          ...base,
          paymentId: base.paymentId || extra.paymentId,
          orderId: base.orderId || extra.orderId,
          method:
            base.method && base.method !== 'Unknown' && base.method !== 'unknown'
              ? base.method
              : extra.method || base.method,
          email: base.email || extra.email,
          contact: base.contact || extra.contact,
          errorCode: base.errorCode || extra.errorCode,
          errorDescription: base.errorDescription || extra.errorDescription,
          capturedAt: base.capturedAt || extra.capturedAt,
        });
      }

      const merged = Array.from(canonical.values());
      // Second pass: collapse Dodo-specific duplicate pairs where one row carries `dodo_sub_*`
      // and another carries `pay_*` for the same checkout window.
      const collapsed: SourceTaggedPayment[] = [];
      const isDodoLike = (row: SourceTaggedPayment) =>
        String(row.method || '').toLowerCase() === 'dodo' ||
        String(row.paymentId || '').toLowerCase().startsWith('dodo_') ||
        String(row.paymentId || '').toLowerCase().startsWith('pay_');
      const statusKey = (v: unknown) => String(v || '').toLowerCase();
      const paymentIdKey = (v: unknown) => String(v || '').toLowerCase();

      for (const row of merged) {
        const rowTime = row.createdAt?.toDate ? row.createdAt.toDate().getTime() : 0;
        const rowAmount = typeof row.amount === 'number' ? Math.round(row.amount) : 0;
        const rowStatus = statusKey(row.status);
        const rowPid = paymentIdKey(row.paymentId);
        const matchIndex = collapsed.findIndex((existing) => {
          if (!isDodoLike(existing) || !isDodoLike(row)) return false;
          const existingTime = existing.createdAt?.toDate ? existing.createdAt.toDate().getTime() : 0;
          const existingAmount = typeof existing.amount === 'number' ? Math.round(existing.amount) : 0;
          const existingStatus = statusKey(existing.status);
          const existingPid = paymentIdKey(existing.paymentId);
          const closeInTime = Math.abs(rowTime - existingTime) <= 15 * 60 * 1000;
          const sameAmount = rowAmount === existingAmount;
          const sameStatus = rowStatus === existingStatus;
          const pidPairLooksLikeSameCheckout =
            (rowPid.startsWith('dodo_sub_') && existingPid.startsWith('pay_')) ||
            (rowPid.startsWith('pay_') && existingPid.startsWith('dodo_sub_'));
          return closeInTime && sameAmount && sameStatus && pidPairLooksLikeSameCheckout;
        });

        if (matchIndex === -1) {
          collapsed.push(row);
          continue;
        }

        const existing = collapsed[matchIndex];
        const rowIsPayId = rowPid.startsWith('pay_');
        const existingIsPayId = paymentIdKey(existing.paymentId).startsWith('pay_');
        const preferred = rowIsPayId && !existingIsPayId ? row : existing;
        const secondary = preferred === row ? existing : row;
        collapsed[matchIndex] = {
          ...preferred,
          paymentId: preferred.paymentId || secondary.paymentId,
          // Keep the alternative identifier for support/debug visibility.
          orderId: preferred.orderId || secondary.orderId || secondary.paymentId,
          method:
            preferred.method && preferred.method !== 'Unknown' && preferred.method !== 'unknown'
              ? preferred.method
              : secondary.method || preferred.method,
          email: preferred.email || secondary.email,
          contact: preferred.contact || secondary.contact,
          errorCode: preferred.errorCode || secondary.errorCode,
          errorDescription: preferred.errorDescription || secondary.errorDescription,
          capturedAt: preferred.capturedAt || secondary.capturedAt,
        };
      }

      collapsed.sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return tb - ta;
      });
      return collapsed.map(({ _source, ...row }) => row as AdminPayment);
    } catch (error) {
      console.error('Error fetching user payment history:', error);
      // Return empty array instead of throwing to gracefully handle missing index
      return [];
    }
  }

  // Get User Invoices (from invoices collection)
  static async getUserInvoices(userId: string): Promise<{
    id: string;
    invoiceNumber: string;
    amount: number;
    currency: string;
    status: string;
    createdAt: Timestamp;
    paidAt?: Timestamp;
    description?: string;
    invoiceUrl?: string;
  }[]> {
    try {
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const invoicesSnapshot = await getDocs(invoicesQuery);

      return invoicesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          invoiceNumber: data.invoice_number || doc.id,
          amount: data.amount_paid || data.amount || 0,
          currency: data.currency || 'USD',
          status: data.status || 'unknown',
          createdAt: data.createdAt || data.created,
          paidAt: data.paidAt,
          description: data.description || 'Subscription payment',
          invoiceUrl: data.hosted_invoice_url || data.invoice_pdf
        };
      });
    } catch (error) {
      console.error('Error fetching user invoices:', error);
      return [];
    }
  }

  // Get email history for a user
  static async getUserEmailHistory(userId: string): Promise<{
    id: string;
    to: string;
    subject: string;
    from: string;
    type: string;
    status: 'sent' | 'failed';
    messageId?: string;
    timestamp: Timestamp;
  }[]> {
    try {
      // First get user's email
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userEmail = userDoc.exists() ? userDoc.data().email : null;

      if (!userEmail) {
        console.warn('User email not found for email history lookup');
        return [];
      }

      // Query system_emails by recipient email
      const emailsQuery = query(
        collection(db, 'email_logs'),
        where('to', '==', userEmail),
        orderBy('timestamp', 'desc')
      );

      const emailsSnapshot = await getDocs(emailsQuery);

      return emailsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          to: data.to,
          subject: data.subject || 'No subject',
          from: data.from || 'system@scubamail.io',
          type: data.type || 'system',
          status: data.status || 'sent',
          messageId: data.messageId,
          timestamp: data.timestamp || data.createdAt
        };
      });
    } catch (error) {
      console.error('Error fetching user email history:', error);
      return [];
    }
  }

  // Send an admin email to a user
  static async sendAdminEmail(params: {
    userId: string;
    userEmail: string;
    subject: string;
    htmlBody: string;
    textBody?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      // Import EmailService dynamically to avoid circular dependency
      const { default: EmailService } = await import('./emailService');

      const result = await EmailService.sendEmail({
        to: params.userEmail,
        subject: params.subject,
        htmlBody: params.htmlBody,
        textBody: params.textBody || params.htmlBody.replace(/<[^>]*>/g, ''),
        fromName: 'Scubamail Admin',
        fromEmail: 'admin@scubamail.io',
        userId: params.userId,
        tags: { Source: 'system', Type: 'admin_message' }
      });

      // Log to system_emails collection
      if (result.success) {
        await addDoc(collection(db, 'email_logs'), {
          to: params.userEmail,
          subject: params.subject,
          from: 'admin@scubamail.io',
          type: 'admin_message',
          status: 'sent',
          messageId: result.messageId,
          timestamp: serverTimestamp(),
          sentBy: 'admin'
        });
      }

      return result;
    } catch (error) {
      console.error('Error sending admin email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email'
      };
    }
  }

  // Force send password reset email to a user (admin action)
  static async forcePasswordReset(userEmail: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Import EmailAuthService dynamically to avoid circular dependency
      const { default: EmailAuthService } = await import('./emailAuthService');

      const result = await EmailAuthService.sendPasswordResetEmailToUser(userEmail);

      if (result.success) {
        // Log admin action to system_emails
        await addDoc(collection(db, 'email_logs'), {
          to: userEmail,
          subject: 'Password Reset Request (Admin Triggered)',
          from: 'system@scubamail.io',
          type: 'admin_password_reset',
          status: 'sent',
          timestamp: serverTimestamp(),
          triggeredBy: 'admin'
        });
      }

      return result;
    } catch (error) {
      console.error('Error forcing password reset:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send password reset email'
      };
    }
  }

  // Manually verify a user's email (admin action)
  static async forceVerifyEmail(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Update the user's emailVerified status in Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        emailVerified: true,
        emailVerifiedAt: serverTimestamp(),
        emailVerifiedBy: 'admin'
      });

      // 2. Update Firebase Auth emailVerified flag via Cloud Function (Admin SDK required)
      // Falls back silently if the function isn't deployed — Firestore update is still written.
      try {
        const adminVerifyEmailFn = httpsCallable(functions, 'adminVerifyUserEmail');
        await adminVerifyEmailFn({ userId });
      } catch (fnError: any) {
        // Log but don't fail — Firestore was already updated, and custom email-gate flows
        // read from the users doc, not from the Auth token.
        console.warn('adminVerifyUserEmail function unavailable (deploy to fix Auth flag):', fnError?.code || fnError?.message);
      }

      // 3. Log the admin action
      await addDoc(collection(db, 'admin_actions'), {
        userId,
        action: 'force_verify_email',
        adminNote: 'Email manually verified by admin',
        timestamp: serverTimestamp(),
        triggeredBy: 'admin'
      });

      return { success: true };
    } catch (error) {
      console.error('Error verifying email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify email'
      };
    }
  }

  // Export all user data for admin download
  static async exportUserData(userId: string): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      // Fetch all user data in parallel
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return { success: false, error: 'User not found' };
      }

      const userData = userDoc.data();
      const userEmail = userData.email;

      // Fetch related data
      const [contacts, emails, payments, sessions] = await Promise.all([
        // Contacts — root collection, not subcollection
        getDocs(query(collection(db, 'contacts'), where('userId', '==', userId))).then(snap =>
          snap.docs.map(d => ({ id: d.id, ...d.data() }))
        ).catch(() => []),

        // Emails sent to user
        this.getUserEmailHistory(userId).catch(() => []),

        // Payments
        this.getUserPaymentHistory(userId).catch(() => []),

        // Sessions — correct collection name
        getDocs(query(collection(db, 'userSessions'), where('userId', '==', userId))).then(snap =>
          snap.docs.map(d => ({ id: d.id, ...d.data() }))
        ).catch(() => [])
      ]);

      // Compile export data
      const exportData = {
        exportDate: new Date().toISOString(),
        userId: userId,
        profile: {
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          displayName: userData.displayName,
          companyName: userData.companyName,
          industry: userData.industry,
          createdAt: userData.createdAt?.toDate?.()?.toISOString() || null,
          emailVerified: userData.emailVerified || false,
          onboardingCompleted: userData.onboardingCompleted || false
        },
        subscription: userData.subscription || null,
        contacts: contacts,
        emailHistory: emails,
        payments: payments.map(p => ({
          ...p,
          createdAt: p.createdAt?.toDate?.()?.toISOString() || null,
          capturedAt: p.capturedAt?.toDate?.()?.toISOString() || null
        })),
        sessions: sessions,
        deviceInfo: userData.deviceInfo || null
      };

      return { success: true, data: exportData };
    } catch (error) {
      console.error('Error exporting user data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to export user data'
      };
    }
  }

  // Sender Management
  static async getAllDomainVerifications(): Promise<any[]> {
    try {
      // SIMPLIFIED: Removing orderBy to bypass index requirement; will sort client-side in the component
      const domainsSnapshot = await getDocs(
        collection(db, 'domainVerifications')
      );

      const domains = [];
      const userCache = new Map<string, string>();
      const workspaceCache = new Map<string, string>();

      for (const domainDoc of domainsSnapshot.docs) {
        const domainData = domainDoc.data();
        let userEmail = 'Unknown';
        let workspaceName = 'Unknown Workspace';

        if (domainData.userId) {
          if (userCache.has(domainData.userId)) {
            userEmail = userCache.get(domainData.userId)!;
          } else {
            try {
              const userDoc = await getDoc(doc(db, 'users', domainData.userId));
              if (userDoc.exists()) {
                userEmail = userDoc.data().email || 'No email';
                userCache.set(domainData.userId, userEmail);
              }
            } catch (e) {
              console.warn(`Failed to get email for user ${domainData.userId}`);
            }
          }
        }

        if (domainData.workspaceId) {
          if (workspaceCache.has(domainData.workspaceId)) {
            workspaceName = workspaceCache.get(domainData.workspaceId)!;
          } else {
            try {
              const wsDoc = await getDoc(doc(db, 'workspaces', domainData.workspaceId));
              if (wsDoc.exists()) {
                workspaceName = wsDoc.data().name || 'Untitled Workspace';
                workspaceCache.set(domainData.workspaceId, workspaceName);
              }
            } catch (e) {
              console.warn(`Failed to get workspace for ${domainData.workspaceId}`);
            }
          }
        }

        domains.push({
          id: domainDoc.id,
          ...domainData,
          userEmail,
          workspaceName
        });
      }
      return domains;
    } catch (error) {
      console.error('Error fetching domain verifications:', error);
      throw error;
    }
  }

  static async getAllEmailVerifications(): Promise<any[]> {
    try {
      // SIMPLIFIED: Removing orderBy to bypass index requirement; will sort client-side in the component
      const emailsSnapshot = await getDocs(
        collection(db, 'emailVerifications')
      );

      const emails = [];
      const userCache = new Map<string, string>();
      const workspaceCache = new Map<string, string>();

      for (const emailDoc of emailsSnapshot.docs) {
        const emailData = emailDoc.data();
        let userEmail = 'Unknown';
        let workspaceName = 'Unknown Workspace';

        if (emailData.userId) {
          if (userCache.has(emailData.userId)) {
            userEmail = userCache.get(emailData.userId)!;
          } else {
            try {
              const userDoc = await getDoc(doc(db, 'users', emailData.userId));
              if (userDoc.exists()) {
                userEmail = userDoc.data().email || 'No email';
                userCache.set(emailData.userId, userEmail);
              }
            } catch (e) {
              console.warn(`Failed to get email for user ${emailData.userId}`);
            }
          }
        }

        if (emailData.workspaceId) {
          if (workspaceCache.has(emailData.workspaceId)) {
            workspaceName = workspaceCache.get(emailData.workspaceId)!;
          } else {
            try {
              const wsDoc = await getDoc(doc(db, 'workspaces', emailData.workspaceId));
              if (wsDoc.exists()) {
                workspaceName = wsDoc.data().name || 'Untitled Workspace';
                workspaceCache.set(emailData.workspaceId, workspaceName);
              }
            } catch (e) {
              console.warn(`Failed to get workspace for ${emailData.workspaceId}`);
            }
          }
        }

        emails.push({
          id: emailDoc.id,
          ...emailData,
          userEmail,
          workspaceName
        });
      }
      return emails;
    } catch (error) {
      console.error('Error fetching email verifications:', error);
      throw error;
    }
  }

  static async getAllSendingDomains(): Promise<any[]> {
    try {
      const domainsSnapshot = await getDocs(
        collection(db, 'sendingDomains')
      );

      const domains = [];
      const userCache = new Map<string, string>();
      const workspaceCache = new Map<string, string>();

      for (const domainDoc of domainsSnapshot.docs) {
        const domainData = domainDoc.data();
        let userEmail = 'Unknown';
        let workspaceName = 'Unknown Workspace';

        if (domainData.userId) {
          if (userCache.has(domainData.userId)) {
            userEmail = userCache.get(domainData.userId)!;
          } else {
            try {
              const userDoc = await getDoc(doc(db, 'users', domainData.userId));
              if (userDoc.exists()) {
                userEmail = userDoc.data().email || 'No email';
                userCache.set(domainData.userId, userEmail);
              }
            } catch (e) {
              console.warn(`Failed to get email for user ${domainData.userId}`);
            }
          }
        }

        if (domainData.workspaceId) {
          if (workspaceCache.has(domainData.workspaceId)) {
            workspaceName = workspaceCache.get(domainData.workspaceId)!;
          } else {
            try {
              const wsDoc = await getDoc(doc(db, 'workspaces', domainData.workspaceId));
              if (wsDoc.exists()) {
                workspaceName = wsDoc.data().name || 'Untitled Workspace';
                workspaceCache.set(domainData.workspaceId, workspaceName);
              }
            } catch (e) {
              console.warn(`Failed to get workspace for ${domainData.workspaceId}`);
            }
          }
        }

        domains.push({
          id: domainDoc.id,
          ...domainData,
          userEmail,
          workspaceName
        });
      }
      return domains;
    } catch (error) {
      console.error('Error fetching sending domains:', error);
      throw error;
    }
  }

  static async getAllEmailMappings(): Promise<any[]> {
    try {
      const mappingsSnapshot = await getDocs(
        collection(db, 'emailAddressMappings')
      );

      const mappings = [];
      const userCache = new Map<string, string>();
      const workspaceCache = new Map<string, string>();

      for (const mappingDoc of mappingsSnapshot.docs) {
        const mappingData = mappingDoc.data();
        let userEmail = 'Unknown';
        let workspaceName = 'Unknown Workspace';

        if (mappingData.userId) {
          if (userCache.has(mappingData.userId)) {
            userEmail = userCache.get(mappingData.userId)!;
          } else {
            try {
              const userDoc = await getDoc(doc(db, 'users', mappingData.userId));
              if (userDoc.exists()) {
                userEmail = userDoc.data().email || 'No email';
                userCache.set(mappingData.userId, userEmail);
              }
            } catch (e) {
              console.warn(`Failed to get email for user ${mappingData.userId}`);
            }
          }
        }

        if (mappingData.workspaceId) {
          if (workspaceCache.has(mappingData.workspaceId)) {
            workspaceName = workspaceCache.get(mappingData.workspaceId)!;
          } else {
            try {
              const wsDoc = await getDoc(doc(db, 'workspaces', mappingData.workspaceId));
              if (wsDoc.exists()) {
                workspaceName = wsDoc.data().name || 'Untitled Workspace';
                workspaceCache.set(mappingData.workspaceId, workspaceName);
              }
            } catch (e) {
              console.warn(`Failed to get workspace for ${mappingData.workspaceId}`);
            }
          }
        }

        mappings.push({
          id: mappingDoc.id,
          email: mappingData.originalEmail, // Standardize field name for UI
          ...mappingData,
          userEmail,
          workspaceName
        });
      }
      return mappings;
    } catch (error) {
      console.error('Error fetching email mappings:', error);
      throw error;
    }
  }

  // Get email services status (SES and Zeptomail)
  static async getEmailServicesStatus(): Promise<EmailServiceStatusResponse> {
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const getStatus = httpsCallable(functions, 'getEmailServicesStatus');
      const result = await getStatus();
      return result.data as EmailServiceStatusResponse;
    } catch (error) {
      console.error('Error fetching email services status:', error);
      throw error;
    }
  }
}
